(function () {
  'use strict';

  if (customElements.get('dd-swatch-selector')) return;

  function formatMoney(cents, fmt, stripGuestHideClass) {
    var amount = (cents / 100).toFixed(2);
    var amountWithCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var out = (fmt || '${{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/, amountWithCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
    if (out.indexOf('hide-price-guest') !== -1 || out.indexOf('&lt;') !== -1) {
      var tmp = document.createElement('div');
      tmp.innerHTML = out;
      out = tmp.textContent || '';
    }
    out = out.replace(/<[^>]*>/g, '').replace(/&lt;[^&]*&gt;/g, '');
    // Strip trailing `.00` / `,00` so whole-dollar prices read as "$180"
    // rather than "$180.00". Negative look-ahead on \d keeps currency
    // suffixes intact (e.g. "$180.00 USD" → "$180 USD"). Non-zero
    // decimals ("$180.50") are untouched.
    return out.replace(/([.,])00(?!\d)/, '');
  }

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  function atcLabelPlainText(span) {
    if (!span) return '';
    var title = span.querySelector('.dd-atc-preorder-title');
    if (title) {
      var by = span.querySelector('.dd-atc-preorder-by');
      return (title.textContent + (by ? ' ' + by.textContent : '')).replace(/\s+/g, ' ').trim();
    }
    var seg = span.querySelector('.dd-atc-button__text-segment');
    if (seg) return (seg.textContent || '').trim();
    return (span.textContent || '').trim();
  }

  function activateAtcLabelMarquee(span, text) {
    var segA = document.createElement('span');
    segA.className = 'dd-atc-button__text-segment';
    segA.textContent = text;
    var segB = segA.cloneNode(true);
    segB.setAttribute('aria-hidden', 'true');
    var track = document.createElement('span');
    track.className = 'dd-atc-button__text-track';
    track.appendChild(segA);
    track.appendChild(segB);
    span.textContent = '';
    span.appendChild(track);
    var durationMs = Math.min(18000, Math.max(8000, text.length * 200));
    span.style.setProperty('--dd-atc-marquee-duration', durationMs / 1000 + 's');
    span.classList.add('dd-atc-button__text--marquee');
  }

  function setPdpAtcPreorder(span, iso) {
    if (!span) return;
    var add = 'Add to cart';
    var root = span.closest ? span.closest('[data-add-text]') : null;
    var fromAttr = root && root.getAttribute('data-add-text');
    if (fromAttr && String(fromAttr).trim()) add = String(fromAttr).trim();
    if (window.ddPreorder && typeof window.ddPreorder.atcFace === 'function') {
      setPdpAtcLabel(span, window.ddPreorder.atcFace(iso, add), true);
      return;
    }
    setPdpAtcLabel(span, add, false);
  }

  function setPdpAtcLabel(span, text, marqueeable) {
    if (!span) return;
    span.classList.remove('dd-atc-button__text--marquee');
    span.style.removeProperty('--dd-atc-marquee-duration');
    span.textContent = text == null ? '' : String(text);
    if (!marqueeable || prefersReducedMotion()) return;
    requestAnimationFrame(function () {
      if (!span.isConnected) return;
      if (span.scrollWidth - 1 <= span.clientWidth) return;
      activateAtcLabelMarquee(span, atcLabelPlainText(span));
    });
  }

  window.ddSetPdpAtcLabel = setPdpAtcLabel;

  class DdSwatchSelector extends HTMLElement {
    constructor() {
      super();
      this.selectedValues = {};
      this.variantData = [];
      // Cap-immune full variant set, populated lazily by
      // _loadFullVariantsAndRefreshHints() via window.ddVariantLoader on
      // products whose variant count hits Shopify's 250-cap. Once present,
      // parseDataSources() prefers it over the (truncated) DOM JSON so
      // every Section Rendering API swap continues to see the full set.
      this._fullVariantData = null;
      this._fullVariantFetchInflight = false;
      // Set when menu-landing needs a stocked deny-policy SKU but the
      // Liquid-capped variantData couldn't find one yet (await GraphQL).
      this._pendingStockedLanding = false;
      this.variantMediaMap = new Map();
      this.optionValueIds = {};
      this.isCommitting = false;
      this.pendingCommit = null;
      this.luminanceCache = new Map();
      this.customOptionSelections = {};
      // Lowercased substring tokens that mark an option value as
      // "custom" (made-to-order via inventory_policy=continue).
      // Source: product.metafields.custom.custom_values, emitted by
      // snippets/dd-swatch-options.liquid as [data-dd-custom-option-names].
      // Used by updateCardHint to keep custom lengths out of the
      // "Available in" badge — mirror of the Liquid filter.
      this.customOptionNameTokens = [];
    }

    isDebugEnabled() {
      return typeof window !== 'undefined' && !!window.__DD_SWATCH_DEBUG;
    }

    debugLog(label, payload) {
      if (!this.isDebugEnabled()) return;
      try {
        console.log('[dd-swatch-selector]', label, payload || {});
      } catch (e) {
        /* no-op */
      }
    }

    connectedCallback() {
      this.sectionId = this.dataset.section;

      this.parseDataSources();
      // Kick off (idempotent, cached for 5min in sessionStorage) GraphQL
      // fetch of the full variant set when the Liquid-rendered JSON looks
      // capped. Resolves async; refreshes hint text on sold-out cards
      // once full data is in. See dd-variant-loader.js + problem-log
      // entries 2026-03-30-001 / 2026-04-15-001 / 2026-05-12-004.
      this._loadFullVariantsAndRefreshHints();
      this.bindNotifyBadgeClicks();
      this.bindSwatchClicks();
      this.bindPillClicks();
      this.initSelectedState();
      this.bindColorFilter();
      this.subscribeToVariantChange();
      this.scheduleLuminanceDetection();
      this.initToggleSliders();

      /*
        Apply availability-only state on initial mount so a
        page loaded with an OOS-but-has-alternatives variant
        renders the in-page ATC clickable-with-scroll like the
        sticky ATC. Defer one frame so product-info / form
        custom elements have upgraded first.
      */
      var self = this;
      requestAnimationFrame(function () {
        var pi = self.closest('product-info');
        var btn = pi ? pi.querySelector('.product-form__submit') : null;
        if (btn) self.applyAtcAvailabilityOnlyState(btn);
        else self.syncInlineBuyButtonsForCombo();
      });
      this._onAtcMarqueeResize = function () {
        if (self._atcMarqueeResizeTimer) clearTimeout(self._atcMarqueeResizeTimer);
        self._atcMarqueeResizeTimer = setTimeout(function () {
          var pi = self.closest('product-info');
          var btn = pi ? pi.querySelector('.product-form__submit') : null;
          if (btn) self.refreshAtcLabelMarquee(btn);
        }, 120);
      };
      window.addEventListener('resize', this._onAtcMarqueeResize);
    }

    parseDataSources() {
      var el;

      el = this.querySelector('[data-dd-custom-option-names]');
      if (el) {
        try {
          var raw = JSON.parse(el.textContent);
          this.customOptionNameTokens = (Array.isArray(raw) ? raw : [])
            .map(function (s) { return String(s == null ? '' : s).trim().toLowerCase(); })
            .filter(function (s) { return s.length > 0; });
        } catch (e) {
          this.customOptionNameTokens = [];
        }
      }

      el = this.querySelector('[data-dd-variant-data]');
      if (el) {
        try { this.variantData = JSON.parse(el.textContent); } catch (e) { /* */ }
      }
      // After every parse (initial mount AND every Section Rendering API
      // swap that calls rebindAfterSwap → parseDataSources), if the full
      // GraphQL variant set has already been fetched, override the DOM
      // JSON which is always capped at 250 by Liquid's product.variants.
      if (this._fullVariantData && this._fullVariantData.length > this.variantData.length) {
        this.variantData = this._fullVariantData;
      }

      el = this.querySelector('[data-dd-variant-media-map]');
      if (el) {
        try {
          var mediaRows = JSON.parse(el.textContent);
          this.variantMediaMap = new Map((mediaRows || []).map(function (row) {
            return [String(row.id), row];
          }));
        } catch (e) { /* */ }
      }

      el = this.querySelector('[data-dd-option-value-ids]');
      if (el) {
        try { this.optionValueIds = JSON.parse(el.textContent); } catch (e) { /* */ }
      }
    }

    initSelectedState() {
      this.syncSelectedStateFromDom();

      this.autoSelectDefaultColor();
      // Menu landings (no ?variant=) must not stay on continue-selling /
      // custom / zero-qty SKUs that Shopify treats as "first available".
      this.ensureStockedLandingVariant();
      this.updateSelectionGate();
      this.setupGateScrollHandler();
      /*
        Don't call recomputePillAvailability / recomputeComboAvailability
        at init — Liquid now emits authoritative is--sold-out /
        is--unavailable / is--combo-unavailable classes driven by
        product_option_value.available + .variant.available, which are
        truncation-proof (work correctly for >250-variant products
        like Keratin Tips). The legacy JS recompute paths operate on
        this.variantData (capped at 250) and would overwrite the
        Liquid state with false positives.
      */
      this.applyCornerBadges();
      this.applyRestockBadges();
      this.applyPreorderBadges();
      if (!this._preorderReadyBound) {
        this._preorderReadyBound = true;
        var selfPo = this;
        document.addEventListener('dd:preorder-map-ready', function () {
          selfPo.applyPreorderBadges();
          selfPo.refreshCornerLuminance();
        });
      }
      /*
        Initial-mount pass for the custom notices: Liquid renders
        `.dd-custom-notice` with is--hidden and the texture expansion
        with display:none unconditionally, so a page that LOADS with a
        custom length/texture already selected (URL variant param,
        selected_or_first_available_variant) needs the same update the
        click path runs — otherwise the disclosures stay hidden until
        the shopper re-selects what's already active.
      */
      this.updateCustomNotices();

      if (window.ddCustomGate && typeof window.ddCustomGate.initDisclosures === 'function') {
        window.ddCustomGate.initDisclosures();
      }
      if (window.ddPreorderGate && typeof window.ddPreorderGate.initDisclosures === 'function') {
        window.ddPreorderGate.initDisclosures();
      }

      if (window.__ddSyncTrustShip && typeof window.__ddSyncTrustShip === 'function') {
        window.__ddSyncTrustShip();
      }
      this.syncDrawerPrices();
    }

    /*
      Re-derive selectedValues and customOptionSelections from the
      freshly-rendered DOM (after a Section Rendering API swap OR
      initial page load). Keeps us aligned with whatever Shopify's
      product.selected_or_first_available_variant resolved to.

      Also includes `[data-dd-drawer-item].is--selected`: when a
      shopper picks a custom-length / custom-texture value, the
      authoritative selection lives on the drawer item (carries
      `data-option-name` + `data-value`), and the parent custom-trigger
      pill mirrors `is--selected` for visual state but does NOT carry
      a `data-value` attribute. Querying only the pill selectors here
      meant `selectedValues['Length']` / `['Texture']` were silently
      empty after every swap for any custom selection — which broke
      the dynamic copy in the combined disclosure (length+texture
      mode would render with the texture half missing because the
      value-string fallback could only see option1 from Liquid).
    */
    syncSelectedStateFromDom() {
      var self = this;
      this.selectedValues = {};
      this.querySelectorAll(
        '[data-dd-swatch-item].is--selected, [data-dd-pill-item].is--selected, [data-dd-drawer-item].is--selected'
      ).forEach(function (el) {
        self.selectedValues[el.dataset.optionName] = el.dataset.value;
      });

      this.customOptionSelections = {};
      this.querySelectorAll('[data-dd-option-group]').forEach(function (group) {
        var optName = group.dataset.optionName;
        if (!optName) return;
        if (self.customOptionNameTokens.length) {
          self.customOptionSelections[optName] = self.isCustomOptionValue(
            self.selectedValues[optName]
          );
        } else {
          var hasStandardSelected = !!group.querySelector(
            '[data-dd-swatch-item].is--selected, [data-dd-pill-item].is--selected'
          );
          self.customOptionSelections[optName] =
            !hasStandardSelected &&
            !!group.querySelector('.dd-option-drawer__item.is--selected');
        }
      });
    }

    applyCornerBadges() {
      this.applyLowStockBadges();
      this.applyNotifyBadges();
      this.applyPreorderBadges();
      /* Corner contrast must run after badges exist so the sampler
         reads the pixels under the real badge box, not a guessed zone.
         Defer past layout — sync refresh on a 0×0 card used to cache
         "dark" and lock white badges onto pale product shots. */
      this.refreshCornerLuminance();
      this.scheduleCornerLuminanceRefresh();
    }

    /*
      ───────────────────────────────────────────────────────
      Notify-me corner badge for sold-out swatches.

      Renders a small "Notify me" pill in the same top-left
      corner-badge stack as low-stock badges. Tapping it:
        1. Selects the swatch (so the notify host gets the
           right variant context via PUB_SUB variantChange).
        2. Opens the existing notify panel by clicking
           whichever notify trigger is currently visible
           (in-page first, sticky-footer fallback).

      Only sold-out cards get the badge. Backorder cards are
      purchasable (Ships 6-8 Weeks) and combo-unavailable
      cards have no underlying SKU — neither makes sense as
      a Klaviyo back-in-stock subscription target.

      The badge is a <span role="button">, not <button>,
      because <button> inside the swatch <button> would be
      invalid nested-interactive-element HTML. Keyboard users
      can still tab to the swatch button itself, which
      selects the variant and reveals the existing in-page
      notify trigger (already keyboard-accessible).
      ───────────────────────────────────────────────────────
    */
    applyNotifyBadges() {
      if (document.documentElement.getAttribute('data-glass-customer') !== 'true') return;
      var cards = this.querySelectorAll('.dd-swatch-group--color [data-dd-swatch-item]');
      if (!cards.length) return;

      // Guest gate is centralized in ensureCornerBadgeHost — for
      // logged-out visitors that returns null, so the early-out
      // below covers it without any duplicate auth check here.
      var self = this;
      cards.forEach(function (card) {
        var corner = self.ensureCornerBadgeHost(card);
        if (!corner) return;

        var existing = corner.querySelector('.dd-swatch-card__notify-badge');
        // Pre-order wins over notify-me: a pre-order variant has a committed
        // ship date and is buyable, so a back-in-stock subscription is the
        // wrong ask. applyPreorderBadges() tears the pill down if the
        // pre-order date hydrates after this pass.
        var show =
          card.classList.contains('is--sold-out') &&
          !card.classList.contains('is--preorder');

        if (show) {
          if (!existing) {
            var badge = self.createNotifyBadge();
            corner.appendChild(badge);
          }
          self._syncNotifyRestockDate(card);
        } else if (existing) {
          existing.remove();
        }
      });
    }

    createNotifyBadge() {
      var badge = document.createElement('span');
      badge.className = 'dd-swatch-card__notify-badge';
      badge.setAttribute('data-dd-swatch-notify', '');
      badge.setAttribute('role', 'button');
      badge.setAttribute('aria-label', 'Notify me when this color is back in stock');

      var iconWrap = document.createElement('span');
      iconWrap.className = 'dd-swatch-card__notify-icon';
      iconWrap.setAttribute('aria-hidden', 'true');
      iconWrap.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none">' +
        '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
        '<path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
        '</svg>';

      var text = document.createElement('span');
      text.className = 'dd-swatch-card__notify-text';
      text.textContent = 'Notify me';

      badge.appendChild(iconWrap);
      badge.appendChild(text);
      return badge;
    }

    /*
      ───────────────────────────────────────────────────────
      Low-stock copy uses the same overlay as
      `.dd-swatch-card__availability-hint` ("Available in…").
      Logged-in only. Sold-out / incoming cards keep their
      existing hint and skip this pass.
      ───────────────────────────────────────────────────────
    */
    applyLowStockBadges() {
      var cards = this.querySelectorAll('.dd-swatch-group--color [data-dd-swatch-item]');
      if (!cards.length) return;
      var self = this;
      cards.forEach(function (card) {
        self._paintLowStockHint(card);
      });
    }

    _paintLowStockHint(card) {
      var leftover = card.querySelector('.dd-swatch-card__low-badge');
      if (leftover) leftover.remove();
      var hintEl = card.querySelector('[data-dd-availability-hint]');
      if (!hintEl) return;
      if (document.documentElement.getAttribute('data-glass-customer') !== 'true') return;

      if (
        card.classList.contains('is--sold-out') ||
        card.classList.contains('is--backorder') ||
        card.classList.contains('is--combo-unavailable') ||
        card.classList.contains('is--preorder')
      ) {
        return;
      }
      var incoming = card.querySelector('.dd-swatch-card__incoming');
      if (incoming && !incoming.hidden) return;

      var LOW_STOCK_THRESHOLD = 5;
      var stockMap = window._ddStockMap || null;
      var variantId = card.dataset.variantId;
      if (variantId && window.ddPreorder && typeof window.ddPreorder.get === 'function' && window.ddPreorder.get(variantId)) {
        return;
      }
      var raw = stockMap && variantId != null ? stockMap[String(variantId)] : null;
      if (typeof raw === 'number' && raw > 0 && raw < LOW_STOCK_THRESHOLD) {
        hintEl.textContent = 'Hurry ' + raw + ' left';
        hintEl.style.display = 'flex';
        return;
      }
      if (/^Hurry \d+ left$/i.test((hintEl.textContent || '').trim())) {
        hintEl.textContent = '';
        hintEl.style.display = 'none';
      }
    }

    ensureCornerBadgeHost(card) {
      var image = card.querySelector('.dd-swatch-card__image');
      if (!image) return null;

      var existing = image.querySelector('[data-dd-corner-badges]');
      if (existing) return existing;
      var host = document.createElement('div');
      host.className = 'dd-swatch-card__corner-badges';
      host.setAttribute('data-dd-corner-badges', '');
      image.insertBefore(host, image.firstChild);
      return host;
    }

    autoSelectDefaultColor() {
      var colorGroup = this.querySelector('.dd-swatch-group--color');
      if (!colorGroup) return;

      // Respect deeplinks: when ?variant= is present, Liquid's
      // product.selected_or_first_available_variant already picked the correct variant
      // and marked the matching swatch is--selected. Don't override it.
      try {
        var params = new URLSearchParams(window.location.search);
        if (params.get('variant')) return;
      } catch (e) { /* URLSearchParams unsupported — fall through to legacy behavior */ }

      // Also respect any existing server-rendered color selection (covers edge cases
      // like collection-card prefilters that pick a non-1N color without ?variant=).
      if (colorGroup.querySelector('[data-dd-swatch-item].is--selected')) return;

      var optionName = colorGroup.dataset.optionName;
      var target = colorGroup.querySelector('[data-dd-swatch-item][data-value="1N"]');
      if (!target) return;

      if (target.classList.contains('is--selected')) return;

      colorGroup.querySelectorAll('[data-dd-swatch-item]').forEach(function (el) {
        el.classList.remove('is--selected');
        el.setAttribute('aria-pressed', 'false');
      });
      target.classList.add('is--selected');
      target.setAttribute('aria-pressed', 'true');

      this.selectedValues[optionName] = '1N';
      this.updateSelectedLabel(optionName, '1N');

      var optionValueId = target.dataset.optionValueId;
      if (optionValueId) this.commitToHiddenPicker(optionName, optionValueId);
    }

    /*
      SuperWeft / Keratin Tips / SecretTapes menu landings should open on
      a genuinely in-stock, non-custom, deny-policy variant — not Shopify's
      selected_or_first_available_variant (which treats continue-selling as
      available and therefore prefers textured/MTO).

      Liquid (snippets/dd-preferred-landing-variant.liquid) redirects when
      it can see a preferred id in the first 250 variants. This is the
      cap-proof safety net: if the current form id is still a continue /
      custom / zero-qty SKU and no ?variant= deeplink is present, pick a
      stocked landing variant and replace the URL once.
    */
    _isStockedLandingProduct() {
      var handle = String(this.dataset.productHandle || '').toLowerCase().trim();
      if (!handle) return false;
      return handle === 'superweft' || handle === 'keratin-tips' || handle.indexOf('secretapes') !== -1;
    }

    _isStockedNonCustomLandingVariant(variant) {
      if (!variant || !variant.available) return false;
      if (String(variant.inventory_policy || '').toLowerCase() === 'continue') return false;
      if (variant.inventory_management && Number(variant.inventory_quantity) <= 0) return false;
      for (var k = 1; k <= 3; k++) {
        var optVal = variant['option' + k];
        if (optVal != null && this.isCustomOptionValue(optVal)) return false;
      }
      return true;
    }

    _findPreferredLandingVariant() {
      var preferred = null;
      var preferred1n = null;
      for (var i = 0; i < this.variantData.length; i++) {
        var v = this.variantData[i];
        if (!this._isStockedNonCustomLandingVariant(v)) continue;
        if (!preferred) preferred = v;
        var is1n = false;
        for (var k = 1; k <= 3; k++) {
          if (String(v['option' + k] || '').trim() === '1N') {
            is1n = true;
            break;
          }
        }
        if (is1n) {
          preferred1n = v;
          break;
        }
      }
      return preferred1n || preferred;
    }

    ensureStockedLandingVariant() {
      if (!this._isStockedLandingProduct()) return;

      try {
        if (new URLSearchParams(window.location.search).get('variant')) return;
      } catch (e) { /* fall through */ }

      var currentId = null;
      var pi = this.closest('product-info');
      var idInput = pi && pi.querySelector('form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] [name="id"]');
      if (!idInput && pi) idInput = pi.querySelector('input[name="id"]');
      if (idInput && idInput.value) currentId = idInput.value;

      var current = null;
      if (currentId) {
        for (var i = 0; i < this.variantData.length; i++) {
          if (String(this.variantData[i].id) === String(currentId)) {
            current = this.variantData[i];
            break;
          }
        }
      }
      if (!current) current = this.findVariantFromSelected();

      if (current && this._isStockedNonCustomLandingVariant(current)) {
        this._pendingStockedLanding = false;
        return;
      }

      var preferred = this._findPreferredLandingVariant();
      if (!preferred) {
        if (this.variantData.length >= 250 && !this._fullVariantData) {
          this._pendingStockedLanding = true;
        }
        return;
      }

      if (current && String(current.id) === String(preferred.id)) {
        this._pendingStockedLanding = false;
        return;
      }

      try {
        var url = new URL(window.location.href);
        if (url.searchParams.get('variant')) return;
        url.searchParams.set('variant', String(preferred.id));
        this._pendingStockedLanding = false;
        window.location.replace(url.pathname + url.search + url.hash);
      } catch (err) {
        try { console.warn('[dd-swatch-selector] stocked landing redirect failed', err); } catch (e) { /* */ }
      }
    }

    getCurrentVariant() {
      return this.findVariantFromSelected();
    }

    findVariantFromSelected() {
      var self = this;
      for (var i = 0; i < this.variantData.length; i++) {
        var v = this.variantData[i];
        var match = true;
        for (var k = 1; k <= 3; k++) {
          var optName = self.getOptionNameByPosition(k);
          if (!optName) continue;
          var sel = self.selectedValues[optName];
          if (!sel) continue;
          if (!self.optionValuesMatch(v['option' + k], sel, optName)) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    }

    findVariantForDrawerItem(optionName, value) {
      var pos = this.getPositionForOptionName(optionName);
      if (!pos || !value) return null;
      var self = this;
      for (var i = 0; i < this.variantData.length; i++) {
        var v = this.variantData[i];
        var match = true;
        for (var k = 1; k <= 3; k++) {
          var optName = self.getOptionNameByPosition(k);
          if (!optName) continue;
          var sel = k === pos ? value : self.selectedValues[optName];
          if (!sel) continue;
          if (!self.optionValuesMatch(v['option' + k], sel, optName)) {
            match = false;
            break;
          }
        }
        if (match) return v;
      }
      return null;
    }

    syncDrawerPrices() {
      var items = this.querySelectorAll('[data-dd-drawer-item]');
      if (!items.length) return;
      var fmt = window.shopMoneyFormat || '${{amount}}';
      var self = this;
      items.forEach(function (item) {
        var priceEl = item.querySelector('.dd-option-drawer__item-price');
        if (!priceEl) return;
        var variant = self.findVariantForDrawerItem(
          item.getAttribute('data-option-name'),
          item.getAttribute('data-value')
        );
        if (!variant || typeof variant.price !== 'number') return;
        priceEl.textContent = formatMoney(variant.price, fmt, true);
        var end = item.querySelector('.dd-option-drawer__item-end');
        if (end) {
          end.querySelectorAll('[data-created-by-regios-dopp], .regios-dopp-description, .regios-dopp-badge').forEach(function (node) {
            node.remove();
          });
        }
      });
    }

    getHiddenVariantSelects() {
      var pi = this.closest('product-info');
      return pi ? pi.querySelector('variant-selects') : null;
    }

    // ── Color collection filter ─────────────────────────

    bindColorFilter() {
      var filter = this.querySelector('[data-dd-color-filter]');
      if (!filter) return;

      filter.addEventListener('change', function () {
        var val = filter.value;
        var groups = this.querySelectorAll('[data-dd-collection-group]');
        groups.forEach(function (group) {
          if (val === 'all' || group.dataset.ddCollectionGroup === val) {
            group.classList.remove('is--hidden');
          } else {
            group.classList.add('is--hidden');
          }
        });
      }.bind(this));
    }

    // ── Click handlers ──────────────────────────────────

    bindSwatchClicks() {
      this.addEventListener('click', function (e) {
        var card = e.target.closest('[data-dd-swatch-item]');
        if (!card) return;
        e.preventDefault();
        this.handleOptionSelect(card);
      }.bind(this));
    }

    /*
      Capture-phase listener: must run BEFORE bindSwatchClicks
      (which is bubble-phase on the same host) so we can take
      ownership of the notify-badge click and prevent the swatch
      handler from also firing. We then synchronously route to
      openNotifyForSwatch which selects the swatch ourselves and
      opens the notify panel once the variantChange propagates.
    */
    bindNotifyBadgeClicks() {
      var self = this;
      this.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-dd-swatch-notify]');
        if (!trigger) return;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        var card = trigger.closest('[data-dd-swatch-item]');
        if (!card) return;
        self.openNotifyForSwatch(card);
      }, true);
    }

    openNotifyForSwatch(card) {
      var self = this;

      /*
        Single-tap flow:
          1. Synchronously commit the swatch selection so the rest
             of the PDP (price, ATC button, dd-notify-when-available
             host, in-page trigger) starts converging on the OOS
             variant immediately.
          2. Scroll the shopper toward the ATC row on the same
             gesture — `.dd-atc-row` is always rendered, so its
             layout box is stable even before the variant change
             flips the notify trigger from `hidden` to visible.
          3. Once variantChange resolves (the notify host has now
             bound to the new variantId and the in-page trigger is
             unhidden), open the panel so the form is already
             expanded by the time the smooth-scroll lands.
      */
      if (!card.classList.contains('is--selected')) {
        self.handleOptionSelect(card);
      }

      self._scrollToNotifyArea();

      var unsub = null;
      var didOpen = false;
      var openOnce = function () {
        if (didOpen) return;
        didOpen = true;
        if (typeof unsub === 'function') unsub();
        requestAnimationFrame(function () { self._openNotifyHost(); });
      };

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        unsub = subscribe(PUB_SUB_EVENTS.variantChange, function (evt) {
          if (evt && evt.data && String(evt.data.sectionId) === String(self.sectionId)) {
            openOnce();
          }
        });
      }
      setTimeout(openOnce, 1800);
    }

    _scrollToNotifyArea() {
      /*
        `.dd-atc-row` is the always-rendered host for both the ATC
        button and the in-page notify trigger; we can't target the
        notify trigger directly here because it may still be
        `hidden` at the moment of the badge click (the variant flip
        from in-stock → OOS is what reveals it, and that happens
        async after the section render API call).
      */
      var target = document.querySelector('.dd-atc-row')
                || document.querySelector('product-form')
                || document.querySelector('[data-dd-pdp-notify-row]');
      if (!target) return;

      var headerOffset = 0;
      try {
        var headerVar = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
        if (headerVar) headerOffset = parseInt(headerVar, 10) || 0;
      } catch (e) { /* */ }

      var rect = target.getBoundingClientRect();
      var top = window.scrollY + rect.top - headerOffset - 24;
      window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
    }

    _openNotifyHost() {
      /*
        Prefer the in-page (PDP) trigger — closest to the swatch grid
        the shopper is interacting with. Fall back to the sticky-
        footer trigger (mobile primarily). Final fallback: invoke
        host.open() directly so the panel still surfaces even if no
        trigger is currently visible.
      */
      var triggers = [
        document.querySelector('.dd-pdp-notify-trigger'),
        document.querySelector('.dd-sticky-footer__notify-trigger')
      ];
      for (var i = 0; i < triggers.length; i++) {
        var t = triggers[i];
        if (t && !t.hidden && !t.disabled && t.offsetParent !== null) {
          t.click();
          return;
        }
      }
      var host = document.querySelector('dd-notify-when-available:not([hidden])')
              || document.querySelector('dd-notify-when-available');
      if (host && typeof host.open === 'function') {
        try { host.open(); } catch (_) { /* noop */ }
      }
    }

    bindPillClicks() {
      this.addEventListener('click', function (e) {
        var pill = e.target.closest('[data-dd-pill-item]');
        if (!pill) return;
        e.preventDefault();
        this.handleOptionSelect(pill);
      }.bind(this));
    }

    handleOptionSelect(el) {
      var optionName = el.dataset.optionName;
      var value = el.dataset.value;
      var optionValueId = el.dataset.optionValueId;

      if (el.classList.contains('is--unavailable') && el.getAttribute('aria-disabled') === 'true') {
        return;
      }

      this.selectedValues[optionName] = value;
      this.customOptionSelections[optionName] = false;
      this.updateVisualSelection(optionName, value);
      var self = this;
      requestAnimationFrame(function () { self._updateToggleSliders(); });
      this.updateSelectedLabel(optionName, value);
      this.updateSelectionGate();
      this.updateCustomNotices();
      this.updateGramDisplay(el);
      this.updateBundleHint(optionName, value);
      this.updateOptionInfo(optionName, value);
      /*
        Skip legacy recompute*; Section Rendering API swap in
        onVariantResolved / onVariantUnavailable will deliver the
        authoritative Liquid-computed state for every card and pill.
        Optimistic price/ATC update still fires via the variantData
        lookup so the button doesn't flicker while the fetch is
        in-flight (falls back gracefully when lookup misses).
      */
      this.applyCornerBadges();
      /* Hide inline ATC immediately when the selected card is already
         marked combo-unavailable (before Section Rendering returns). */
      this.syncInlineBuyButtonsForCombo();
      var variant = this.findVariantFromSelected();
      if (variant) this.applyOptimisticUpdates(variant);
      this.commitToHiddenPicker(optionName, optionValueId);
    }

    updateVisualSelection(optionName, value) {
      var group = this.querySelector('[data-dd-option-group][data-option-name="' + optionName + '"]');
      if (!group) return;

      group.querySelectorAll('[data-dd-swatch-item], [data-dd-pill-item]').forEach(function (el) {
        var isMatch = el.dataset.value === value;
        el.classList.toggle('is--selected', isMatch);
        el.setAttribute('aria-pressed', String(isMatch));
      });

      var customTrigger = group.querySelector('[data-dd-custom-trigger]');
      if (customTrigger) {
        customTrigger.classList.remove('is--selected');
        customTrigger.setAttribute('aria-expanded', 'false');
        var isTextureOption = /texture/i.test(optionName);
        var isLengthOption = /length/i.test(optionName);
        var idleLabel = isTextureOption
          ? 'Custom Curls'
          : isLengthOption
            ? 'Custom Length'
            : 'Custom';
        var triggerText = customTrigger.querySelector('.dd-swatch-pill__text');
        if (triggerText) {
          triggerText.textContent = idleLabel;
        } else {
          var raw = customTrigger.childNodes[0];
          if (raw && raw.nodeType === 3) {
            raw.textContent = idleLabel + ' ';
          }
        }
        var triggerBadge = customTrigger.querySelector('.dd-swatch-pill__badge');
        if (triggerBadge) triggerBadge.remove();
      }
      group.querySelectorAll('.dd-option-drawer__item').forEach(function (el) {
        el.classList.remove('is--selected');
      });
    }

    updateSelectedLabel(optionName, value) {
      var group = this.querySelector('[data-dd-option-group][data-option-name="' + optionName + '"]');
      if (!group) return;
      var label = group.querySelector('[data-dd-selected-label]');
      if (!label) return;

      var isLength = /length|inch/i.test(optionName);
      var display = value.replace(/"/g, '');
      if (isLength) display += ' inches';

      var selectedEl = group.querySelector('.is--selected[data-dd-swatch-item], .is--selected[data-dd-pill-item]');
      if (selectedEl && selectedEl.dataset.nickname) {
        display += ' (' + selectedEl.dataset.nickname + ')';
      }

      var strong = label.querySelector('strong');
      if (!strong) {
        /* Build via createElement/textContent (not innerHTML string concat)
           so option values and swatch nicknames can never be parsed as
           markup. */
        label.textContent = '';
        strong = document.createElement('strong');
        label.appendChild(strong);
      }
      strong.textContent = display;
      if (display) strong.setAttribute('title', display);
      else strong.removeAttribute('title');
    }

    updateGramDisplay(selectedEl) {
      var gramAmount = selectedEl.dataset.gramAmount;
      var group = selectedEl.closest('[data-dd-option-group]');
      if (!group) return;
      var gramSpan = group.querySelector('[data-dd-gram-display]');
      if (!gramSpan) return;

      if (gramAmount) {
        var display = gramAmount;
        if (display.toLowerCase().indexOf('gram') === -1 && display.toLowerCase().indexOf('g') === -1) {
          display += ' grams';
        }
        gramSpan.textContent = '| ' + display;
        gramSpan.style.display = '';
      } else {
        gramSpan.style.display = 'none';
      }
    }

    /*
      Per-product bundle recommendation lookup, JS side.

      Mirrors the per-product Liquid case in
      `snippets/dd-bundle-recommendation.liquid` — KEEP BOTH IN SYNC.

      Returns one of:
        - null            when no recommendation applies
        - { target, label, unit, phrase, caption }
            target  number   button qty (defaults to upper end of range)
            label   string   strong-emphasis text e.g. "1\u20132", "3", "3\u20134"
            unit    string   "bundles" / "packs"
            phrase  string   trailing phrase e.g. "for full transformation"
            caption string   composed full caption
                             ("3\u20134 bundles for full transformation")
    */
    static getBundleRecommendation(value, productHandle) {
      if (value === undefined || value === null || value === '') return null;
      var clean = String(value)
        .replace(/"/g, '')
        .replace(/inches/gi, '')
        .replace(/inch/gi, '')
        .trim();
      var num = parseFloat(clean);
      if (!isFinite(num) || num <= 0) return null;

      var rec = null;
      var unit = 'bundles';
      var handle = (productHandle || '').toLowerCase();

      if (handle === 'superweft') {
        unit = 'bundles';
        if (num >= 25)      rec = { target: 4, label: '4',         phrase: 'for full transformation' };
        else if (num >= 23) rec = { target: 4, label: '3\u20134',  phrase: 'for full transformation' };
        else if (num >= 21) rec = { target: 3, label: '3',         phrase: 'for full coverage' };
        else if (num >= 19) rec = { target: 2, label: '2',         phrase: 'for medium coverage' };
        else if (num >= 14) rec = { target: 2, label: '1\u20132',  phrase: 'for fill or natural enhancement' };
      } else if (handle === 'keratin-tips') {
        unit = 'packs';
        if (num >= 25)      rec = { target: 12, label: '8\u201312', phrase: 'for a full install \u2014 length + volume' };
        else if (num >= 21) rec = { target: 6,  label: '4\u20136',  phrase: 'for standard length enhancement' };
        else if (num >= 17) rec = { target: 4,  label: '3\u20134',  phrase: 'for a partial install' };
        else if (num >= 12) rec = { target: 3,  label: '2\u20133',  phrase: 'for volume / density enhancement' };
      } else {
        /* secretapes and any other product fall back to the generic
           weft heuristic until merchant-specified values are added
           to the per-product case here AND in the snippet. */
        unit = handle === 'secretapes' ? 'packs' : 'bundles';
        if (num >= 26)      rec = { target: 3, label: '3', phrase: 'for full transformation' };
        else if (num >= 22) rec = { target: 2, label: '2', phrase: 'for full coverage' };
        else if (num <= 20) rec = { target: 1, label: '1', phrase: 'for fill or natural enhancement' };
      }

      if (!rec) return null;
      rec.unit = unit;
      rec.caption = rec.label + ' ' + unit + ' ' + rec.phrase;
      return rec;
    }

    /*
      Optimistic client-side update for the inline bundle hint that
      sits next to "Selected 26 inches | 75g" in the length swatch
      group. The Section Rendering API swap will eventually deliver
      authoritative state from Liquid, but until it lands the hint
      should reflect the user's just-clicked length immediately.
    */
    updateBundleHint(optionName, value) {
      var group = this.querySelector('[data-dd-option-group][data-option-name="' + optionName + '"]');
      if (!group) return;
      if (!/length|inch/i.test(optionName)) return;
      var hint = group.querySelector('[data-dd-bundle-hint]');
      if (!hint) return;

      var handle = this.dataset.productHandle || '';
      var rec = this.constructor.getBundleRecommendation(value, handle);
      var caption = hint.querySelector('[data-dd-bundle-inline-caption]');
      if (!rec) {
        hint.hidden = true;
        if (caption) caption.textContent = '';
        hint.setAttribute('data-dd-bundle-target', '0');
        return;
      }
      hint.hidden = false;
      hint.setAttribute('data-dd-bundle-target', String(rec.target));
      hint.setAttribute('data-dd-bundle-label', rec.label);
      if (rec.unit) hint.setAttribute('data-dd-bundle-unit', rec.unit);
      hint.setAttribute('data-dd-bundle-caption', rec.caption);
      if (caption) caption.textContent = rec.caption;
    }

    // ── Selection gate ──────────────────────────────────

    updateSelectionGate() {
      var allOptions = this.querySelectorAll('[data-dd-option-group]');
      var missing = null;

      for (var i = 0; i < allOptions.length; i++) {
        var name = allOptions[i].dataset.optionName;
        if (!this.selectedValues[name]) {
          missing = name;
          break;
        }
      }

      var productInfo = this.closest('product-info');
      var submitBtn = productInfo ? productInfo.querySelector('.product-form__submit') : null;

      if (missing) {
        if (submitBtn) submitBtn.setAttribute('data-dd-gate', missing);
      } else {
        if (submitBtn) submitBtn.removeAttribute('data-dd-gate');
      }

      this.updateCustomCTAText(missing, submitBtn);
    }

    setupGateScrollHandler() {
      var self = this;
      var productInfo = this.closest('product-info');
      if (!productInfo) return;
      var submitBtn = productInfo.querySelector('.product-form__submit');
      if (!submitBtn || submitBtn._ddGateHandlerBound) return;
      submitBtn._ddGateHandlerBound = true;

      submitBtn.addEventListener('click', function () {
        var missing = submitBtn.getAttribute('data-dd-gate');
        if (!missing) return;

        var groups = self.querySelectorAll('[data-dd-option-group]');
        for (var i = 0; i < groups.length; i++) {
          if (groups[i].dataset.optionName === missing) {
            groups[i].scrollIntoView({ behavior: 'smooth', block: 'center' });
            groups[i].classList.remove('dd-swatch-group--attention');
            /* Trigger reflow so re-adding the class restarts the animation */
            void groups[i].offsetWidth;
            groups[i].classList.add('dd-swatch-group--attention');
            break;
          }
        }
      });
    }

    updateCustomCTAText(missingOption, submitBtn) {
      if (!submitBtn) return;

      var textEl = submitBtn.querySelector('.dd-atc-button__text') || submitBtn.querySelector('span');

      if (missingOption) {
        setPdpAtcLabel(textEl, 'Select ' + missingOption, false);
        submitBtn.setAttribute('aria-disabled', 'true');
        return;
      }

      submitBtn.removeAttribute('aria-disabled');

      var hasCustom = this.isCustomSelection();
      if (textEl && hasCustom) {
        setPdpAtcLabel(textEl, 'Place Custom Order', false);
      } else if (textEl) {
        var v = this.getCurrentVariant && this.getCurrentVariant();
        var iso = v && window.ddPreorder ? window.ddPreorder.get(v.id) : null;
        if (iso) setPdpAtcPreorder(textEl, iso);
        else setPdpAtcLabel(textEl, 'Add to cart', false);
      }
    }

    // ── Option info modules (texture/color info) ──────

    updateOptionInfo(optionName, value) {
      var group = this.querySelector('[data-dd-option-group][data-option-name="' + optionName + '"]');
      if (!group) return;

      var infoEl = group.querySelector('[data-dd-option-info]');
      if (!infoEl) return;

      var textureData = this.querySelector('[data-dd-texture-info]');
      if (!textureData) {
        infoEl.style.display = 'none';
        return;
      }

      try {
        var infoMap = JSON.parse(textureData.textContent);
        var entry = infoMap[value] || infoMap[value.toLowerCase()];
        if (entry) {
          /* textContent, not innerHTML: texture_info is a merchant
             metafield (admin-gated), but descriptions are plain text —
             no reason to open an HTML sink for them. */
          infoEl.textContent = entry;
          infoEl.style.display = '';
        } else {
          infoEl.style.display = 'none';
        }
      } catch (e) {
        infoEl.style.display = 'none';
      }
    }

    // ── Custom notices ──────────────────────────────────

    /*
      Mirrors Liquid + dd-nav-link-href: any selected option value that
      matches product.metafields.custom.custom_values (via the JSON
      tokens in data-dd-custom-option-names). Drawer DOM state alone
      can drift after Section Rendering swaps or deep-linked standard
      variants, which previously marked in-stock Straight combos as
      "Custom item · ships 6-8 weeks" on nav landings.
    */
    isCustomOptionValue(value) {
      if (value === undefined || value === null || value === '') return false;
      var val = String(value).toLowerCase().trim();
      if (!val) return false;
      var tokens = this.customOptionNameTokens || [];
      for (var i = 0; i < tokens.length; i++) {
        var token = String(tokens[i] == null ? '' : tokens[i]).trim().toLowerCase();
        if (token && val.indexOf(token) !== -1) return true;
      }
      return false;
    }

    isCustomSelection() {
      if (this.customOptionNameTokens.length) {
        var keys = Object.keys(this.selectedValues || {});
        for (var i = 0; i < keys.length; i++) {
          if (this.isCustomOptionValue(this.selectedValues[keys[i]])) return true;
        }
        return false;
      }
      var customKeys = Object.keys(this.customOptionSelections || {});
      for (var j = 0; j < customKeys.length; j++) {
        if (this.customOptionSelections[customKeys[j]]) return true;
      }
      return false;
    }

    /*
      True only when EVERY option group has a committed value. Needed
      because findVariantFromSelected() skips unselected options, so on
      a partial selection it "resolves" the first variant matching the
      chosen subset. Anything that acts on the lookup optimistically
      (form id, ATC enable) must gate on this first.
    */
    isSelectionComplete() {
      var self = this;
      var complete = true;
      this.querySelectorAll('[data-dd-option-group]').forEach(function (g) {
        var optName = g.dataset.optionName;
        if (optName && !self.selectedValues[optName]) complete = false;
      });
      return complete;
    }

    hasCustomTextureSelected() {
      var sel = this.customOptionSelections || {};
      var keys = Object.keys(sel);
      for (var i = 0; i < keys.length; i++) {
        if (keys[i].toLowerCase() === 'texture' && sel[keys[i]]) return true;
      }
      return false;
    }

    /*
      ─────────────────────────────────────────────────────────────────
      Custom-order disclosure state machine

      Three mutually exclusive surfaces so legal copy never doubles:

        length-only  → `.dd-custom-notice` (amber card between Length
                       and Texture groups). Label "Custom length".
        texture-only → `.dd-texture-expansion` (care copy + amber
                       "Custom texture" card under the Texture group).
        both         → `.dd-texture-expansion` as the singular combined
                       surface. Amber badge becomes "Custom length and
                       texture"; `.dd-custom-notice` stays hidden.
                       Selection values stay on the pills (not repeated
                       in the amber card).

      Detection mirrors the same is--selected DOM markers used by
      `applyCornerBadges` / `isCustomSelection`. Runs on every swatch
      click, on initSelectedState, and after Section Rendering API
      swaps (rebindAfterSwap).
      ─────────────────────────────────────────────────────────────────
    */
    getCustomDisclosureState() {
      var lengthGroup = this.querySelector('.dd-swatch-group--length');
      var isCustomLength = false;
      if (lengthGroup) {
        var lengthName = lengthGroup.dataset.optionName;
        if (this.customOptionNameTokens.length && lengthName) {
          isCustomLength = this.isCustomOptionValue(this.selectedValues[lengthName]);
        } else {
          var trigger = lengthGroup.querySelector('[data-dd-custom-trigger].is--selected');
          var drawerItem = lengthGroup.querySelector('.dd-option-drawer__item.is--selected');
          isCustomLength = !!(trigger || drawerItem);
        }
      }

      var textureGroup = this.querySelector(
        '[data-dd-option-group][data-option-name="Texture"], [data-dd-option-group][data-option-name="texture"]'
      );
      var isCustomTexture = false;
      if (textureGroup) {
        var textureName = textureGroup.dataset.optionName;
        if (this.customOptionNameTokens.length && textureName) {
          isCustomTexture = this.isCustomOptionValue(this.selectedValues[textureName]);
        } else {
          var texSelectedItem = textureGroup.querySelector('.dd-option-drawer__item.is--selected');
          var texCustomTrigger = textureGroup.querySelector('[data-dd-custom-trigger][aria-expanded="true"]');
          isCustomTexture = !!(texSelectedItem || texCustomTrigger);
        }
      }

      return {
        lengthGroup: lengthGroup,
        textureGroup: textureGroup,
        isCustomLength: isCustomLength,
        isCustomTexture: isCustomTexture,
        mode: isCustomLength && isCustomTexture
          ? 'both'
          : isCustomLength
            ? 'length'
            : isCustomTexture
              ? 'texture'
              : 'none'
      };
    }

    updateCustomNotices() {
      var state = this.getCustomDisclosureState();
      var notice = this.querySelector('.dd-custom-notice');

      /* Standalone notice is length-only. Combined mode lives inside
         the texture expansion so education + legal stay one surface. */
      if (notice) {
        if (state.mode === 'length') {
          var lengthValue = state.lengthGroup
            ? this.selectedValues[state.lengthGroup.dataset.optionName]
            : '';

          var labelEl = notice.querySelector('[data-dd-custom-label]');
          if (labelEl) labelEl.textContent = 'Custom length';

          var valEl = notice.querySelector('[data-dd-custom-value]') ||
            notice.querySelector('[data-dd-custom-length]');
          if (valEl && lengthValue) valEl.textContent = lengthValue;

          notice.classList.remove('is--hidden');
          notice.removeAttribute('hidden');
        } else {
          notice.classList.add('is--hidden');
        }
      }

      this.updateTextureExpansion(state);
      this.updateSpecChipLabels();

      if (window.ddCustomGate && typeof window.ddCustomGate.refresh === 'function') {
        window.ddCustomGate.refresh();
      }
      if (window.ddPreorderGate && typeof window.ddPreorderGate.refresh === 'function') {
        window.ddPreorderGate.refresh();
      }

      if (window.__ddSyncTrustShip && typeof window.__ddSyncTrustShip === 'function') {
        window.__ddSyncTrustShip();
      }
    }

    /*
      Spec chips live outside the Section Rendering swap list, so a
      custom length/texture pick must rewrite the duration chip client-
      side. Only chips that emitted data-dd-chip-label-custom (today:
      the SuperWeft "12+ MONTH RATED" pill) participate.
    */
    updateSpecChipLabels() {
      var root = this.closest('product-info') || document;
      var chips = root.querySelectorAll('[data-dd-chip-label-custom]');
      if (!chips.length) return;

      var isCustom = this.isCustomSelection();
      for (var i = 0; i < chips.length; i++) {
        var chip = chips[i];
        var labelEl =
          chip.querySelector('.dd-pdp-spec-chip__label') ||
          chip.querySelector('.dd-pdp-badge__label');
        if (!labelEl) continue;

        var next = isCustom
          ? chip.getAttribute('data-dd-chip-label-custom')
          : chip.getAttribute('data-dd-chip-label-std');
        if (next && labelEl.textContent !== next) {
          labelEl.textContent = next;
        }
      }
    }

    updateTextureExpansion(state) {
      state = state || this.getCustomDisclosureState();
      var textureGroup = state.textureGroup;
      if (!textureGroup) return;

      var expansion = textureGroup.querySelector('[data-dd-texture-expansion]');
      if (!expansion) return;

      var showExpansion = state.mode === 'texture' || state.mode === 'both';
      expansion.style.display = showExpansion ? '' : 'none';
      if (!showExpansion) return;

      /* Badge only — selection values already read on the pills above. */
      var badgeEl = expansion.querySelector('[data-dd-texture-warning-badge]');
      if (badgeEl) {
        badgeEl.textContent = state.mode === 'both'
          ? 'Custom length and texture'
          : 'Custom texture';
      }
    }

    // ── Shadow pattern: commit to hidden <variant-selects> ──

    commitToHiddenPicker(optionName, optionValueId) {
      if (this.isCommitting) {
        this.pendingCommit = { optionName: optionName, optionValueId: optionValueId };
        return;
      }

      this.isCommitting = true;

      try {
        var vs = this.getHiddenVariantSelects();
        if (!vs) {
          this.isCommitting = false;
          return;
        }

        var input = vs.querySelector('[data-option-value-id="' + optionValueId + '"]');
        if (!input) {
          this.isCommitting = false;
          return;
        }

        if (input.tagName === 'INPUT') {
          input.checked = true;
        } else if (input.tagName === 'OPTION') {
          input.selected = true;
        }

        input.dispatchEvent(new Event('change', { bubbles: true }));

        /*
          That dispatch synchronously ran Dawn's handleOptionValueChange
          (product-info.js), which disabled the main ATC for the whole
          Section Rendering API round-trip. That conservatism predates
          the optimistic form-id write in applyOptimisticUpdates: by this
          point input[name="id"] already carries the newly resolved
          variant, so a click during the fetch adds the correct variant.
          Re-enable immediately when the client-side lookup resolves an
          available variant from a COMPLETE, non-custom selection.
          Anything else (partial selection, custom drawer values, OOS,
          lookup miss) keeps Dawn's disabled state until the server
          render paints the authoritative face. Worst case is inventory
          that sold out after page load: the click fails at /cart/add
          with the stock-error toast, same as a shopper who idled on an
          enabled button.
        */
        if (this.isSelectionComplete() && !this.isCustomSelection()) {
          var optimisticVariant = this.findVariantFromSelected();
          if (optimisticVariant && optimisticVariant.available) {
            var pi = this.closest('product-info');
            var pf = pi && pi.productForm;
            /*
              The form-id check is load-bearing, not paranoia: some
              commit callers (autoSelectDefaultColor on page load) never
              ran applyOptimisticUpdates, so the form can still carry the
              pre-commit variant. Enabling then would let a click add the
              WRONG variant (the exact bug this whole change removes).
              Only enable when the form already says what we resolved.
            */
            var idInput = pf && pf.form && pf.form.querySelector('input[name="id"]');
            if (
              idInput &&
              String(idInput.value) === String(optimisticVariant.id) &&
              typeof pf.toggleSubmitButton === 'function'
            ) {
              pf.toggleSubmitButton(false);
            }
          }
        }
      } catch (err) {
        console.error('[dd-swatch-selector] commit error', err);
      } finally {
        this.isCommitting = false;

        if (this.pendingCommit) {
          var pending = this.pendingCommit;
          this.pendingCommit = null;
          this.commitToHiddenPicker(pending.optionName, pending.optionValueId);
        }
      }
    }

    // ── Subscribe to variantChange (from product-info) ──

    subscribeToVariantChange() {
      if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        if (event.data.sectionId !== this.sectionId) return;

        var variant = event.data.variant;
        var html = event.data.html;

        if (variant) {
          this.onVariantResolved(variant, html);
        } else {
          this.onVariantUnavailable(html);
        }
      }.bind(this));
    }

    applyOptimisticUpdates(variant) {
      if (!variant) return;
      var productInfo = this.closest('product-info');
      if (!productInfo) return;
      var sid = productInfo.dataset.section;

      /*
        Commit the resolved variant id to the product form(s) NOW.
        product-info's updateVariantInputs writes the same value again
        after the Section Rendering API round-trip, but that leaves a
        fetch-length window where the form still carries the previous
        variant: a fast in-page ATC click (or the sticky footer's
        getVariantIdFromForm fallback) would add the OLD variant even
        though every visible surface already shows the new one. Same
        selector pair as updateVariantInputs in product-info.js.

        Gated on isSelectionComplete: with a partial selection the
        variant we were handed is a first-match heuristic (see the
        helper's comment), fine for cosmetic price/label previews but
        not for the form's semantic id.
      */
      if (this.isSelectionComplete()) {
        productInfo.querySelectorAll(
          '#product-form-' + sid + ', #product-form-installment-' + sid
        ).forEach(function (productForm) {
          var idInput = productForm.querySelector('input[name="id"]');
          if (idInput && String(idInput.value) !== String(variant.id)) {
            idInput.value = String(variant.id);
            idInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      var fmt = window.shopMoneyFormat || '${{amount}}';
      var isCustom = this.isCustomSelection();
      var preorderIso = (window.ddPreorder && typeof window.ddPreorder.get === 'function')
        ? window.ddPreorder.get(variant.id)
        : null;
      var isRestocking = !preorderIso && !variant.available;
      if (!isRestocking && !preorderIso && !isCustom && variant.inventory_management === 'shopify' && variant.inventory_quantity <= 0) {
        isRestocking = true;
      }

      // ── Price row (skip if discount pill is active — dd-discount-pill.js owns it) ──
      var discountActive = document.querySelector('[data-dd-discount-pill-footer]');
      var pillVisible = discountActive && discountActive.style.display !== 'none';
      if (!pillVisible) {
        var priceRow = productInfo.querySelector('#dd-title-price-' + sid);
        if (priceRow) {
          var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          var saleEl = priceRow.querySelector('[data-dd-pdp-pro-sale]');
          if (saleEl) {
            saleEl.textContent = formatMoney(variant.price, fmt);
            saleEl.classList.toggle('dd-pro-price__sale--discounted', onSale);
          }
          var msrpWrap = priceRow.querySelector('[data-dd-pdp-msrp-wrap]');
          var msrpEl = priceRow.querySelector('[data-dd-pdp-msrp]');
          if (msrpWrap) msrpWrap.style.display = onSale ? '' : 'none';
          if (msrpEl) msrpEl.textContent = onSale ? formatMoney(variant.compare_at_price, fmt) : '';
        }
      }

      // ── Stock status ──
      var stockEl = productInfo.querySelector('#dd-stock-status-' + sid);
      if (stockEl) {
        var txt = stockEl.querySelector('.dd-stock-status__text');
        if (txt) {
          var paint = window.DDShipCountdown && window.DDShipCountdown.paintPdpStockText;
          var isDigital = variant.requires_shipping === false;
          var isEventPdp = !!document.querySelector(
            '.product--tpl-event-certification, dd-sticky-footer.dd-sticky-footer--event'
          );
          if (isEventPdp) {
            var eventLeft = 0;
            var eventTracked = false;
            var eventMap = window._ddStockMap;
            if (eventMap) {
              Object.keys(eventMap).forEach(function (id) {
                var q = eventMap[id];
                if (q == null || isNaN(q)) return;
                eventTracked = true;
                eventLeft += Math.max(0, Number(q));
              });
            }
            var eventLabel = 'Tickets available';
            if (eventTracked && eventLeft <= 0) eventLabel = 'Sold out';
            else if (eventTracked && eventLeft === 1) eventLabel = '1 ticket left';
            else if (eventTracked) eventLabel = eventLeft + ' tickets left';
            if (paint) paint(txt, 'event', { label: eventLabel });
            else txt.textContent = eventLabel;
          } else if (isRestocking) {
            var soldTxt = (typeof window.getRestockStatusLabel === 'function')
              ? window.getRestockStatusLabel(variant)
              : (typeof window.getRestockLabel === 'function')
              ? window.getRestockLabel(variant)
              : 'Sold out';
            if (paint) paint(txt, 'soldout', { label: soldTxt });
            else txt.textContent = soldTxt;
          } else if (preorderIso) {
            var poNote = (window.ddPreorder && typeof window.ddPreorder.shipNote === 'function')
              ? window.ddPreorder.shipNote(preorderIso)
              : '';
            if (paint) paint(txt, 'preorder', { note: poNote });
            else txt.textContent = poNote || 'Estimated to ship. Subject to delays.';
          } else if (isCustom) {
            if (paint) paint(txt, 'custom');
            else txt.textContent = 'Made to order. 6-8 weeks';
          } else if (isDigital) {
            if (paint) paint(txt, 'digital');
            else txt.textContent = 'Instantly available';
          } else {
            var qty = (window._ddStockMap && window._ddStockMap[String(variant.id)] != null)
              ? window._ddStockMap[String(variant.id)]
              : variant.inventory_quantity;
            if (qty != null && qty > 0 && qty < 5) {
              if (paint) paint(txt, 'lowstock', { count: qty });
              else txt.textContent = 'Hurry ' + qty + ' left. ' + ((window.DDShipCountdown && window.DDShipCountdown.getPillInfo) ? window.DDShipCountdown.getPillInfo().label : 'Ships soon');
            } else if (paint) {
              paint(txt, 'instock');
            } else {
              txt.textContent = '';
              var lead = document.createElement('span');
              lead.setAttribute('data-dd-sf-ship-instock-lead', '');
              lead.textContent = 'In stock. ';
              var countEl = document.createElement('span');
              countEl.setAttribute('data-dd-ship-countdown', '');
              txt.appendChild(lead);
              txt.appendChild(countEl);
            }
          }
        }
      }

      // ── ATC button ──
      var submitBtn = productInfo.querySelector('.product-form__submit');
      if (submitBtn && !submitBtn.hasAttribute('data-dd-gate')) {
        var span = submitBtn.querySelector('.dd-atc-button__text');
        if (span) {
          if (isRestocking) {
            var restockLabel = this.getAvailableLengthsText() ||
              ((typeof window.getRestockHeadline === 'function')
                ? window.getRestockHeadline(variant)
                : 'Sold out');
            setPdpAtcLabel(span, restockLabel, /^Available in /i.test(restockLabel));
          } else if (preorderIso) {
            setPdpAtcPreorder(span, preorderIso);
            submitBtn.classList.add('is--preorder-label');
          } else if (isCustom) {
            setPdpAtcLabel(span, 'Place Custom Order', false);
            submitBtn.classList.remove('is--preorder-label');
          } else {
            setPdpAtcLabel(span, 'Add to cart', false);
            submitBtn.classList.remove('is--preorder-label');
          }
          if (isRestocking) submitBtn.classList.remove('is--preorder-label');
        }
        this.updateAtcButtonPrice(submitBtn, variant);
        this.applyAtcAvailabilityOnlyState(submitBtn);
      }

      // ── Gallery ──
      var gallery = productInfo.querySelector('media-gallery');
      if (gallery && typeof gallery._applyVariantFilter === 'function') {
        gallery._applyVariantFilter(String(variant.id), variant);
      }
    }

    onVariantResolved(variant, html) {
      this.swapSwatchOptionsFromHtml(html);
      this.applyCornerBadges();

      var productInfo = this.closest('product-info');
      var submitBtn = productInfo ? productInfo.querySelector('.product-form__submit') : null;

      if (submitBtn && !submitBtn.hasAttribute('data-dd-gate')) {
        var isCustom = this.isCustomSelection();
        var span = submitBtn.querySelector('.dd-atc-button__text');
        if (span) {
          if (!variant.available) {
            var oosLabel = this.getAvailableLengthsText() ||
              ((typeof window.getRestockHeadline === 'function')
                ? window.getRestockHeadline(variant)
                : 'Sold out');
            setPdpAtcLabel(span, oosLabel, /^Available in /i.test(oosLabel));
            submitBtn.classList.remove('is--preorder-label');
          } else if (window.ddPreorder && window.ddPreorder.get(variant.id)) {
            setPdpAtcPreorder(span, window.ddPreorder.get(variant.id));
            submitBtn.classList.add('is--preorder-label');
          } else if (isCustom) {
            setPdpAtcLabel(span, 'Place Custom Order', false);
            submitBtn.classList.remove('is--preorder-label');
          } else {
            setPdpAtcLabel(span, 'Add to cart', false);
            submitBtn.classList.remove('is--preorder-label');
          }
        }
      }

      this.updateAtcButtonPrice(submitBtn, variant);
      this.applyAtcAvailabilityOnlyState(submitBtn);
    }

    onVariantUnavailable(html) {
      this.swapSwatchOptionsFromHtml(html);
      this.applyCornerBadges();

      var productInfo = this.closest('product-info');
      var submitBtn = productInfo ? productInfo.querySelector('.product-form__submit') : null;
      if (submitBtn) {
        var missing = null;
        var groups = this.querySelectorAll('[data-dd-option-group]');
        for (var i = 0; i < groups.length; i++) {
          if (!this.selectedValues[groups[i].dataset.optionName]) {
            missing = groups[i].dataset.optionName;
            break;
          }
        }
        if (missing) {
          var span = submitBtn.querySelector('.dd-atc-button__text');
          if (span) setPdpAtcLabel(span, 'Select ' + missing, false);
        }
      }
      this.updateAtcButtonPrice(submitBtn, null);
      this.applyAtcAvailabilityOnlyState(submitBtn);
    }

    /*
      ───────────────────────────────────────────────────────
      In-page ATC parity with sticky ATC: when the current
      variant is sold out but other lengths are available,
      keep the button clickable so a click scrolls back up
      to the Length chooser. Visually it still reads as
      disabled (opacity, no submit) via aria-disabled.

      product-form.js's onSubmitHandler bails on
      aria-disabled === 'true', so the form never submits
      while we're in this state. Our click listener
      (capture phase) intercepts the click and scrolls.
      ───────────────────────────────────────────────────────
    */
    /*
      Hide the inline buy-buttons block when the selected combo isn't
      offered. The swatch overlay already states "not offered with
      texture", and the sticky footer carries the "Not offered" status
      face — keeping MTO acceptance + a dead ATC inline is noise.
      Restores the block as soon as the shopper picks a sellable combo.
    */
    syncInlineBuyButtonsForCombo() {
      var comboGone = this.isCurrentComboUnavailable();
      document.documentElement.classList.toggle('dd-combo-unavailable', comboGone);

      var block = document.querySelector('.dd-block--buy-buttons');
      if (block) {
        if (comboGone) {
          block.setAttribute('hidden', '');
          block.setAttribute('aria-hidden', 'true');
        } else {
          block.removeAttribute('hidden');
          block.removeAttribute('aria-hidden');
        }
      }

      if (window.ddCustomGate && typeof window.ddCustomGate.refresh === 'function') {
        window.ddCustomGate.refresh();
      }
      if (window.ddPreorderGate && typeof window.ddPreorderGate.refresh === 'function') {
        window.ddPreorderGate.refresh();
      }
    }

    applyAtcAvailabilityOnlyState(submitBtn) {
      this.syncInlineBuyButtonsForCombo();
      if (!submitBtn || submitBtn.hasAttribute('data-dd-gate')) return;
      var currentVariant = null;
      try {
        currentVariant = typeof this.getCurrentVariant === 'function'
          ? this.getCurrentVariant()
          : null;
      } catch (e) { /* */ }
      var isDisabled = currentVariant
        ? currentVariant.available === false
        : (submitBtn.disabled
          || submitBtn.hasAttribute('disabled')
          || submitBtn.classList.contains('is--availability-only'));

      /*
        Combo-unavailable is a TERMINAL disabled state — the brand
        doesn't produce this color × current length/texture combo, so
        neither bouncing the user up to the Length picker
        (availability-only mode) nor surfacing a Notify-me makes sense.
        Force fully-disabled, override the label to "Not offered", and
        clear the ship-date subtitle. This runs on every variant-change
        path because applyAtcAvailabilityOnlyState is invoked at the end
        of applyOptimisticUpdates and onVariantResolved, so the override
        wins over whichever "Awaiting Restock" / "Available in …" string
        was just written by the existing branches above.

        Mirrors the sticky ATC's not-offered branch in
        assets/dd-sticky-footer.js (syncButtonFromRenderedHtml +
        updateShippingAndCustom). Single signal source —
        isCurrentComboUnavailable() reads the swatch DOM the shopper
        already sees, so any future change to the brand convention
        (deny-policy + OOS + custom-eligible product) lives in
        setSoldOutState alone, not duplicated across surfaces.

        The PDP-inline notify trigger / row are suppressed by
        assets/dd-pdp-notify-inline.js's syncVisibility, which reads the
        same signal. We don't touch them here because the notify-inline
        bridge owns that DOM and its MutationObserver-driven state
        machine — having two writers race over hidden attrs would
        produce flicker on rapid swatch clicks.

        The whole `.dd-block--buy-buttons` shell (acceptance + ATC) is
        hidden via syncInlineBuyButtonsForCombo() above — this branch
        still labels the (hidden) button so a later unhide is correct.
      */
      var isComboUnavailable = this.isCurrentComboUnavailable();
      if (isComboUnavailable) {
        submitBtn.disabled = true;
        submitBtn.classList.remove('is--availability-only');
        submitBtn.removeAttribute('aria-disabled');
        var notOfferedSpan = submitBtn.querySelector('.dd-atc-button__text');
        if (notOfferedSpan) setPdpAtcLabel(notOfferedSpan, 'Not offered', false);
        return;
      }

      var availText = isDisabled ? this.getAvailableLengthsText() : null;
      var availabilityOnly = isDisabled && !!availText;

      if (availabilityOnly) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('disabled');
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.classList.add('is--availability-only');
        var availSpan = submitBtn.querySelector('.dd-atc-button__text');
        if (availSpan) setPdpAtcLabel(availSpan, availText, true);
      } else {
        submitBtn.classList.remove('is--availability-only');
        if (submitBtn.getAttribute('aria-disabled') === 'true') {
          submitBtn.removeAttribute('aria-disabled');
        }
        this.refreshAtcLabelMarquee(submitBtn);
      }
      this.bindAvailabilityOnlyClick(submitBtn);
    }

    refreshAtcLabelMarquee(submitBtn) {
      var span = submitBtn && submitBtn.querySelector('.dd-atc-button__text');
      if (!span) return;
      var text = atcLabelPlainText(span);
      var marqueeable = submitBtn.classList.contains('is--preorder-label') ||
        (submitBtn.classList.contains('is--availability-only') && /^Available in /i.test(text));
      setPdpAtcLabel(span, text, marqueeable);
    }

    bindAvailabilityOnlyClick(submitBtn) {
      if (!submitBtn || submitBtn._ddAvailOnlyClickBound) return;
      submitBtn._ddAvailOnlyClickBound = true;
      var self = this;
      submitBtn.addEventListener('click', function (e) {
        if (!submitBtn.classList.contains('is--availability-only')) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        self.scrollToOptionGroup('Length');
      }, true);
    }

    scrollToOptionGroup(optionName) {
      var target = null;
      var lower = (optionName || '').toLowerCase();
      var groups = this.querySelectorAll('[data-dd-option-group]');
      for (var i = 0; i < groups.length; i++) {
        if ((groups[i].dataset.optionName || '').toLowerCase().indexOf(lower) !== -1) {
          target = groups[i];
          break;
        }
      }
      if (!target) target = this;

      var headerOffset = 0;
      try {
        var headerVar = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
        if (headerVar) headerOffset = parseInt(headerVar, 10) || 0;
      } catch (e) { /* */ }

      var rect = target.getBoundingClientRect();
      var top = window.scrollY + rect.top - headerOffset - 16;
      window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
    }

    /*
      ───────────────────────────────────────────────────────
      Swap [data-dd-swatch-options] with the server-rendered
      block from the Section Rendering API response (delivered
      by product-info.js via the variantChange pub/sub event).

      Liquid is the authoritative source of truth for
      availability state on every card and pill — driven by
      product_option_value.available + .variant.available,
      which don't have the 250-variant cap that
      product.variants enforces. On products with more than
      250 theoretical variants (e.g. Keratin Tips at ~896),
      this is the only way to get correct "Awaiting Restock"
      badging and "Available in X, Y" hints without
      mis-marking available variants as OOS.
      ───────────────────────────────────────────────────────
    */
    swapSwatchOptionsFromHtml(html) {
      if (!html || !html.querySelector) return;

      var selector = 'dd-swatch-selector[data-section="' + this.sectionId + '"] [data-dd-swatch-options]';
      var fresh = html.querySelector(selector) || html.querySelector('[data-dd-swatch-options]');
      if (!fresh) return;

      var current = this.querySelector('[data-dd-swatch-options]');
      if (!current) return;

      current.replaceWith(fresh);
      this.rebindAfterSwap();
    }

    rebindAfterSwap() {
      this.syncSelectedStateFromDom();
      this.parseDataSources();
      this.initToggleSliders();
      this.scheduleLuminanceDetection();
      this.applyCornerBadges();
      this._updateToggleSliders(true);
      /* The Section Rendering API swap replaces `.dd-custom-notice` with
         a fresh `is--hidden` element, so re-apply visibility + copy from
         the just-resynced selection state. Without this call the notice
         silently dropped after every variant change — even when the user
         had a custom selection that should have kept it visible. */
      this.updateCustomNotices();
      if (window.ddCustomGate && typeof window.ddCustomGate.initDisclosures === 'function') {
        window.ddCustomGate.initDisclosures();
      }
      if (window.ddPreorderGate && typeof window.ddPreorderGate.initDisclosures === 'function') {
        window.ddPreorderGate.initDisclosures();
      }
      // The freshly-rendered swatch options carry Liquid-built (capped)
      // hint text. If the full variant set is already loaded, refresh
      // hints on the swapped-in cards immediately so 22"/24" etc. don't
      // silently disappear from "Available in" labels after a length
      // pill click. If not yet loaded, kick the fetch off (idempotent
      // due to sessionStorage caching) so a later swap will benefit.
      if (this._fullVariantData) {
        this._refreshSoldOutCardHints();
        this.syncDrawerPrices();
      } else {
        this._loadFullVariantsAndRefreshHints();
      }
    }

    updateAtcButtonPrice(submitBtn, variant) {
      if (!submitBtn) return;
      var mainRow = submitBtn.querySelector('.dd-atc-button__main') || submitBtn;
      var priceEl = submitBtn.querySelector('[data-dd-atc-price]');
      var dashEl = submitBtn.querySelector('.dd-atc-button__dash');
      var productInfo = this.closest('product-info');
      var allowGuestPrice =
        !!(productInfo && productInfo.dataset && productInfo.dataset.guestColorRingPurchase === 'true');

      if (!variant || !variant.available || submitBtn.classList.contains('is--preorder-label')) {
        if (priceEl) priceEl.hidden = true;
        if (dashEl) dashEl.hidden = true;
        return;
      }

      var fmt = window.shopMoneyFormat || '${{amount}}';
      var hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;

      if (!priceEl) {
        priceEl = document.createElement('span');
        priceEl.className = allowGuestPrice ? 'dd-atc-button__price' : 'dd-atc-button__price hide-price-guest';
        priceEl.setAttribute('data-dd-atc-price', '');
        mainRow.appendChild(priceEl);
        if (!dashEl) {
          dashEl = document.createElement('span');
          dashEl.className = 'dd-atc-button__dash';
          dashEl.setAttribute('aria-hidden', 'true');
          dashEl.textContent = '-';
          mainRow.insertBefore(dashEl, priceEl);
        }
      }

      if (dashEl) dashEl.hidden = false;
      priceEl.hidden = false;
      if (allowGuestPrice) priceEl.classList.remove('hide-price-guest');

      var compareHtml = hasCompare
        ? '<span class="dd-atc-button__compare">' + formatMoney(variant.compare_at_price, fmt, allowGuestPrice) + '</span>'
        : '';
      priceEl.innerHTML = compareHtml + '<span class="dd-atc-button__current">' + formatMoney(variant.price, fmt, allowGuestPrice) + '</span>';
    }

    // ── Pill availability engine ───────────────────────────

    recomputePillAvailability(sourceRoot) {
      if (!this.variantData.length) return;

      var pillGroups = this.querySelectorAll('[data-dd-option-group]');
      var self = this;

      pillGroups.forEach(function (group) {
        var groupOptName = group.dataset.optionName;
        if (!groupOptName) return;
        var groupOptNameLower = groupOptName.toLowerCase();
        if (groupOptNameLower === 'color' || groupOptNameLower === 'colour' || groupOptNameLower === 'colors') return;

        // Per UX: non-color pills (length, texture, etc.) never render in a
        // disabled visual state. Only color swatch cards reflect OOS / combo-
        // unavailable. Actively scrub any stale state to keep cached SSR HTML
        // and any prior JS-applied flags aligned with the new behavior.
        var pills = group.querySelectorAll('[data-dd-pill-item]');
        pills.forEach(function (pill) {
          if (pill.classList.contains('is--unavailable')) {
            pill.classList.remove('is--unavailable');
          }
          if (pill.classList.contains('is--combo-unavailable')) {
            pill.classList.remove('is--combo-unavailable');
          }
          if (pill.hasAttribute('aria-disabled')) {
            pill.removeAttribute('aria-disabled');
          }
        });
      });
    }

    getOptionAvailabilityMapFromVariantSelects(sourceRoot, targetOptionName) {
      if (!sourceRoot || !targetOptionName || !sourceRoot.querySelector) return null;

      var variantSelects = null;
      if (sourceRoot.matches && sourceRoot.matches('variant-selects')) {
        variantSelects = sourceRoot;
      } else {
        variantSelects = sourceRoot.querySelector('variant-selects');
      }
      if (!variantSelects) return null;

      var targetNorm = String(targetOptionName || '').toLowerCase().trim();
      var map = new Map();
      var controls = variantSelects.querySelectorAll('[data-option-value-id]');

      controls.forEach(function (control) {
        var optionName = this.getOptionNameForHiddenControl(control);
        if (!optionName) return;
        if (String(optionName).toLowerCase().trim() !== targetNorm) return;

        var rawValue = this.getOptionValueForHiddenControl(control);
        if (!rawValue) return;

        var key = this.normalizeOptionValue(rawValue, targetOptionName);
        if (!key) return;

        map.set(key, !this.isHiddenControlUnavailable(control));
      }.bind(this));

      return map.size ? map : null;
    }

    getEffectiveOptionAvailabilityMap(sourceRoot, targetOptionName) {
      var fromSource = this.getOptionAvailabilityMapFromVariantSelects(sourceRoot, targetOptionName);
      if (fromSource) return fromSource;

      var hiddenVariantSelects = this.getHiddenVariantSelects();
      return this.getOptionAvailabilityMapFromVariantSelects(hiddenVariantSelects, targetOptionName);
    }

    getOptionNameForHiddenControl(control) {
      if (!control) return '';

      if (control.tagName === 'INPUT') {
        var inputName = String(control.getAttribute('name') || '').trim();
        if (!inputName) return '';
        return inputName.replace(/-\d+$/, '').trim();
      }

      if (control.tagName === 'OPTION') {
        var select = control.closest('select');
        var selectName = select ? String(select.getAttribute('name') || '').trim() : '';
        var match = selectName.match(/^options\[(.*)\]$/);
        return match ? match[1].trim() : '';
      }

      return '';
    }

    getOptionValueForHiddenControl(control) {
      if (!control) return '';
      return String(control.value || '').trim();
    }

    isHiddenControlUnavailable(control) {
      if (!control) return true;
      if (control.disabled) return true;
      if (String(control.getAttribute('aria-disabled') || '').toLowerCase() === 'true') return true;
      if (control.classList) {
        // Shopify's swatch-input snippet marks unavailable color inputs with
        // `visually-disabled` (no `disabled` attribute). Treat it as unavailable
        // so the authoritative availability map reflects Shopify's context-aware
        // state for color swatches.
        if (control.classList.contains('disabled')) return true;
        if (control.classList.contains('visually-disabled')) return true;
      }

      if (control.tagName === 'OPTION') {
        var text = String(control.textContent || '').toLowerCase();
        if (text.indexOf('unavailable') !== -1 || text.indexOf('sold out') !== -1) return true;
      }

      return false;
    }

    // ── Dynamic combo availability engine ─────────────────

    recomputeComboAvailability(sourceRoot) {
      if (!this.variantData.length) return;

      var colorGroup = this.getColorGroup();
      if (!colorGroup) return;

      var self = this;
      var cards = colorGroup.querySelectorAll('[data-dd-swatch-item]');
      var colorOptName = colorGroup.dataset.optionName;
      var colorPos = this.getPositionForOptionName(colorOptName);
      var isCustomTexture = this.hasCustomTextureSelected();
      var isCustom = this.isCustomSelection();

      this.debugLog('recompute:start', {
        selectedValues: this.selectedValues,
        colorPosition: colorPos,
        isCustomTexture: isCustomTexture,
        isCustom: isCustom,
        variantCount: this.variantData.length
      });

      cards.forEach(function (card) {
        var colorVal = card.dataset.value;

        card.classList.remove('dd-hidden-custom');
        self.setNoTextureState(card, false);
        card.style.removeProperty('display');
        self.removeTextureNotice(card);

        if (isCustomTexture) {
          var canSell = self.hasContinueSellingVariantForColor(colorVal, colorPos);
          if (!canSell) {
            self.setLeadTimeBadge(card, false);
            self.setSoldOutState(card, false);
            self.setNoTextureState(card, true);
            self.showTextureNotice(card);
            return;
          }
          self.setNoTextureState(card, false);
          self.setSoldOutState(card, false);
          self.setLeadTimeBadge(card, true);
          self.updateCardHint(card, colorVal, colorPos, true);
        } else if (isCustom) {
          self.setNoTextureState(card, false);
          self.setSoldOutState(card, false);
          self.setLeadTimeBadge(card, true);
          self.updateCardHint(card, colorVal, colorPos, true);
        } else {
          self.setNoTextureState(card, false);
          /*
            Per-combo availability must come from the JSON variant data —
            Shopify's option_value.available (which drives the hidden
            <variant-selects> .visually-disabled class) aggregates across
            ALL variants for that color and is not context-aware for the
            currently selected length + texture. We need the specific
            length+texture+color variant's `available` flag to determine
            whether the user can add-to-cart this exact combo.

            BUT: "combo not available" is NOT the same as "Awaiting
            Restock". On sparse-variant products like K-tips, most
            (length × color × texture) combos don't exist at all —
            e.g. 22" only ships in 1N, so 2N–9N have no 22"/Straight
            variant to be "awaiting restock" on. Showing the restock
            badge on those cards is misleading because those colors
            ARE in stock, just at other lengths.

            Split the reason-code:
              - comboAvailable        → current combo can be purchased
              - colorHasStockAnywhere → color has ≥1 in-stock variant
                                        anywhere in its catalog
            Then:
              - combo available                → no overlay
              - combo unavailable + color has
                stock elsewhere                → "Available in X, Y"
                                                 plus the restock date
                                                 for THIS length+color
              - combo unavailable + color has
                no stock anywhere              → restock date for
                                                 this length+color, or
                                                 "Awaiting Restock"
          */
          var comboVariants = self.getMatchingVariantsForColor(colorVal, colorPos);
          var comboAvailable = self.hasAvailableVariant(comboVariants);
          var colorHasStockAnywhere = self.hasAnyAvailableVariantForColor(colorVal, colorPos);
          self.setSoldOutState(card, !comboAvailable, {
            colorHasStockElsewhere: !comboAvailable && colorHasStockAnywhere,
            restockVariantId: !comboAvailable
              ? self.pickRestockVariantId(comboVariants, colorVal, colorPos)
              : null,
            restockVariant: !comboAvailable
              ? self.pickRestockVariant(comboVariants, colorVal, colorPos)
              : null
          });
          self.setLeadTimeBadge(card, false);
          self.updateCardHint(card, colorVal, colorPos, comboAvailable);
        }
      });
      this.applyPreorderBadges();
      this.refreshCornerLuminance();
    }

    setNoTextureState(card, isDisabled) {
      if (!card) return;
      card.classList.toggle('is--no-texture', !!isDisabled);

      if (isDisabled) {
        card.setAttribute('aria-disabled', 'true');
        card.dataset.available = 'false';
        return;
      }

      if (!card.classList.contains('is--sold-out')) {
        card.removeAttribute('aria-disabled');
        card.dataset.available = 'true';
      }
    }

    comboVariantExists(colorVal, colorPos) {
      return this.hasContinueSellingVariant(this.getMatchingVariantsForColor(colorVal, colorPos));
    }

    isCustomSellableVariant(variant) {
      var policy = String((variant && variant.inventory_policy) || '').toLowerCase();
      return policy === 'continue';
    }

    isComboAvailable(colorVal, colorPos) {
      return this.hasAvailableVariant(this.getMatchingVariantsForColor(colorVal, colorPos));
    }

    getMatchingVariantsForColor(colorVal, colorPos) {
      var self = this;
      var matches = [];
      for (var i = 0; i < this.variantData.length; i++) {
        var v = this.variantData[i];
        if (!self.optionValuesMatch(v['option' + colorPos], colorVal, self.getOptionNameByPosition(colorPos))) continue;

        var isMatch = true;
        for (var k = 1; k <= 3; k++) {
          if (k === colorPos) continue;
          var optName = self.getOptionNameByPosition(k);
          if (!optName) continue;
          var selectedVal = self.selectedValues[optName];
          if (!selectedVal) continue;
          if (!self.optionValuesMatch(v['option' + k], selectedVal, optName)) {
            isMatch = false;
            break;
          }
        }
        if (isMatch) matches.push(v);
      }
      return matches;
    }

    hasContinueSellingVariant(variants) {
      for (var i = 0; i < variants.length; i++) {
        if (this.isCustomSellableVariant(variants[i])) return true;
      }
      return false;
    }

    hasContinueSellingVariantForColor(colorVal, colorPos) {
      var colorOptionName = this.getOptionNameByPosition(colorPos);
      for (var i = 0; i < this.variantData.length; i++) {
        var v = this.variantData[i];
        if (!this.optionValuesMatch(v['option' + colorPos], colorVal, colorOptionName)) continue;
        if (this.isCustomSellableVariant(v)) return true;
      }
      return false;
    }

    hasAvailableVariant(variants) {
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].available) return true;
      }
      return false;
    }

    /*
      Does the given color have ANY in-stock variant at all — ignoring
      the currently selected length/texture? Used to differentiate
      "this color is temporarily out of stock everywhere" (Awaiting
      Restock) from "this color just isn't sold at the current
      length/texture combo" (available-elsewhere hint).
    */
    hasAnyAvailableVariantForColor(colorVal, colorPos) {
      var colorOptName = this.getOptionNameByPosition(colorPos);
      for (var i = 0; i < this.variantData.length; i++) {
        var v = this.variantData[i];
        if (!this.optionValuesMatch(v['option' + colorPos], colorVal, colorOptName)) continue;
        if (v.available) return true;
      }
      return false;
    }

    getVariantsForColor(colorVal, colorPos) {
      var colorOptName = this.getOptionNameByPosition(colorPos);
      var matches = [];
      for (var i = 0; i < this.variantData.length; i++) {
        var v = this.variantData[i];
        if (!this.optionValuesMatch(v['option' + colorPos], colorVal, colorOptName)) continue;
        matches.push(v);
      }
      return matches;
    }

    pickRestockVariant(comboVariants, colorVal, colorPos) {
      if (typeof window.ddResolveRestockVariant === 'function') {
        return window.ddResolveRestockVariant({
          colorVal: colorVal,
          colorPos: colorPos,
          comboVariants: comboVariants
        });
      }
      return (comboVariants && comboVariants[0]) || null;
    }

    pickRestockVariantId(comboVariants, colorVal, colorPos) {
      var v = this.pickRestockVariant(comboVariants, colorVal, colorPos);
      return v && v.id != null ? v.id : null;
    }

    comboRestockSettled(comboVariants) {
      if (window._ddRestockFetchDone) return true;
      var map = window._ddRestockDateMap;
      if (!map) return false;
      var list = comboVariants || [];
      var seen = 0;
      for (var i = 0; i < list.length; i++) {
        var v = list[i];
        if (!v || v.id == null) continue;
        var oos = v.available === false ||
          (v.inventory_management === 'shopify' &&
            typeof v.inventory_quantity === 'number' &&
            v.inventory_quantity <= 0 &&
            v.inventory_policy !== 'continue');
        if (!oos) continue;
        seen += 1;
        if (!Object.prototype.hasOwnProperty.call(map, String(v.id))) return false;
      }
      return seen > 0;
    }

    applyRestockBadges() {
      var colorGroup = this.getColorGroup();
      if (!colorGroup) return;
      var colorPos = this.getPositionForOptionName(colorGroup.dataset.optionName);
      var cards = colorGroup.querySelectorAll('[data-dd-swatch-item]');
      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        var leftover = card.querySelector('.dd-swatch-card__restock-badge');
        if (leftover) leftover.remove();
        var colorVal = card.dataset.value;
        var comboVariants = colorPos
          ? this.getMatchingVariantsForColor(colorVal, colorPos)
          : [];
        var preorder = false;
        if (window.ddPreorder && typeof window.ddPreorder.get === 'function') {
          for (var p = 0; p < comboVariants.length; p++) {
            if (window.ddPreorder.get(comboVariants[p].id)) {
              preorder = true;
              break;
            }
          }
        }
        var soldOut = card.classList.contains('is--sold-out');
        var restockVariant = soldOut && !preorder
          ? this.pickRestockVariant(comboVariants, colorVal, colorPos)
          : null;
        var id = restockVariant && restockVariant.id;
        if (id != null) card.dataset.restockVariantId = String(id);
        else delete card.dataset.restockVariantId;
        if (restockVariant && restockVariant.sku) card.dataset.restockSku = String(restockVariant.sku);
        else delete card.dataset.restockSku;
        var dateMd = (!preorder && soldOut && typeof window.getRestockNumericLabel === 'function')
          ? window.getRestockNumericLabel(restockVariant || id)
          : '';
        var notify = card.querySelector('.dd-swatch-card__notify-badge');
        if (notify) {
          this._paintNotifyDate(card, dateMd);
          this._paintRestockCorner(card, '');
        } else {
          this._paintRestockCorner(card, dateMd ? 'BACK BY ' + dateMd : '');
        }
      }
    }

    _notifyRestockKey(card) {
      var id = card && card.dataset ? card.dataset.restockVariantId : '';
      var sku = card && card.dataset ? card.dataset.restockSku : '';
      if (id) return { id: id, sku: sku || undefined };
      if (sku) return { sku: sku };
      return null;
    }

    _syncNotifyRestockDate(card) {
      var dateMd = '';
      if (typeof window.getRestockNumericLabel === 'function') {
        dateMd = window.getRestockNumericLabel(this._notifyRestockKey(card));
      }
      this._paintNotifyDate(card, dateMd);
      this._paintRestockCorner(card, '');
    }

    _paintNotifyDate(card, dateMd) {
      var badge = card.querySelector('.dd-swatch-card__notify-badge');
      if (!badge) return;
      var textEl = badge.querySelector('.dd-swatch-card__notify-text');
      if (!textEl) return;
      var labelNode = textEl.querySelector('.dd-swatch-card__notify-label');
      var dateNode = textEl.querySelector('.dd-swatch-card__notify-date');
      if (!labelNode) {
        textEl.textContent = '';
        labelNode = document.createElement('span');
        labelNode.className = 'dd-swatch-card__notify-label';
        textEl.appendChild(labelNode);
      }
      labelNode.textContent = 'Notify me';
      if (dateMd) {
        if (!dateNode) {
          dateNode = document.createElement('span');
          dateNode.className = 'dd-swatch-card__notify-date';
          textEl.appendChild(dateNode);
        }
        dateNode.textContent = dateMd;
      } else if (dateNode) {
        dateNode.remove();
      }
      badge.setAttribute(
        'aria-label',
        dateMd
          ? 'Notify me when this color is back in stock ' + dateMd
          : 'Notify me when this color is back in stock'
      );
    }

    _paintRestockCorner(card, label) {
      var corner = this.ensureCornerBadgeHost(card);
      var badge = card.querySelector('.dd-swatch-card__restock');
      if (!label) {
        if (badge) {
          badge.hidden = true;
          var empty = badge.querySelector('.dd-swatch-card__restock-text');
          if (empty) empty.textContent = '';
        }
        return;
      }
      if (!badge && corner) {
        badge = this.createRestockCornerBadge();
        var notify = corner.querySelector('.dd-swatch-card__notify-badge');
        if (notify) corner.insertBefore(badge, notify);
        else corner.appendChild(badge);
      }
      if (!badge) return;
      var textEl = badge.querySelector('.dd-swatch-card__restock-text');
      if (textEl) textEl.textContent = label;
      badge.setAttribute('aria-label', label);
      badge.hidden = false;
    }

    applyPreorderBadges() {
      var colorGroup = this.getColorGroup();
      if (!colorGroup) return;
      var colorPos = this.getPositionForOptionName(colorGroup.dataset.optionName);
      var cards = colorGroup.querySelectorAll('[data-dd-swatch-item]');
      var getter = window.ddPreorder && typeof window.ddPreorder.get === 'function'
        ? window.ddPreorder.get.bind(window.ddPreorder)
        : null;
      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        var image = card.querySelector('.dd-swatch-card__image');
        if (!image) continue;
        var corner = this.ensureCornerBadgeHost(card);
        var incoming = image.querySelector('.dd-swatch-card__incoming');
        if (incoming && corner && !incoming.closest('[data-dd-corner-badges]')) {
          corner.insertBefore(incoming, corner.firstChild);
        }
        var chip = image.querySelector('.dd-swatch-card__preorder-badge');
        if (chip) chip.remove();
        var iso = null;
        if (getter) {
          var comboVariants = colorPos
            ? this.getMatchingVariantsForColor(card.dataset.value, colorPos)
            : [];
          for (var j = 0; j < comboVariants.length; j++) {
            iso = getter(comboVariants[j].id);
            if (iso) break;
          }
        }
        card.classList.toggle('is--preorder', !!iso);
        if (iso) {
          if (!incoming && corner) {
            incoming = this.createIncomingBadge();
            corner.insertBefore(incoming, corner.firstChild);
          } else if (incoming && this._incomingBadgeNeedsRebuild(incoming)) {
            var next = this.createIncomingBadge();
            incoming.parentNode.replaceChild(next, incoming);
            incoming = next;
          }
          if (incoming) {
            incoming.hidden = false;
            var label =
              window.ddPreorder && typeof window.ddPreorder.incomingFace === 'function'
                ? window.ddPreorder.incomingFace(iso)
                : 'INCOMING';
            var textEl = incoming.querySelector('.dd-swatch-card__incoming-text');
            if (textEl) textEl.textContent = label;
            incoming.setAttribute('aria-label', label);
          }
          var restock = card.querySelector('.dd-swatch-card__restock');
          if (restock) restock.hidden = true;
          var lead = image.querySelector('.dd-swatch-card__lead-badge');
          if (lead) lead.classList.remove('is-visible');
          var low = corner && corner.querySelector('.dd-swatch-card__low-badge');
          if (low) low.remove();
          // Pre-order takes precedence over the notify-me pill (see
          // applyNotifyBadges). Remove it if the notify pass ran first.
          var notifyPill = corner && corner.querySelector('.dd-swatch-card__notify-badge');
          if (notifyPill) notifyPill.remove();
          if (colorPos) this.updateCardHint(card, card.dataset.value, colorPos, true);
        } else {
          if (incoming) incoming.hidden = true;
        }
      }
    }

    _incomingBadgeNeedsRebuild(el) {
      if (!el) return true;
      if (el.querySelector('.dd-swatch-card__incoming-dot')) return true;
      var wrap = el.querySelector('.dd-swatch-card__incoming-icon');
      if (!wrap) return true;
      return wrap.tagName === 'svg' || wrap.tagName === 'SVG';
    }

    createIncomingIcon() {
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '1.8');
      svg.setAttribute('stroke-linecap', 'round');
      svg.setAttribute('stroke-linejoin', 'round');
      var ns = 'http://www.w3.org/2000/svg';
      function path(d) {
        var el = document.createElementNS(ns, 'path');
        el.setAttribute('d', d);
        svg.appendChild(el);
      }
      path('M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5');
      path('M16 2v4');
      path('M8 2v4');
      path('M3 10h5');
      path('M17.5 17.5 16 16.25V14');
      var circle = document.createElementNS(ns, 'circle');
      circle.setAttribute('cx', '16');
      circle.setAttribute('cy', '16');
      circle.setAttribute('r', '6');
      svg.appendChild(circle);
      return svg;
    }

    createIncomingBadge() {
      var badge = document.createElement('span');
      badge.className = 'dd-swatch-card__incoming';
      badge.setAttribute('aria-label', 'INCOMING');

      var iconWrap = document.createElement('span');
      iconWrap.className = 'dd-swatch-card__incoming-icon';
      iconWrap.setAttribute('aria-hidden', 'true');
      iconWrap.appendChild(this.createIncomingIcon());

      var text = document.createElement('span');
      text.className = 'dd-swatch-card__incoming-text';
      text.textContent = 'INCOMING';

      badge.appendChild(iconWrap);
      badge.appendChild(text);
      return badge;
    }

    createRestockCornerBadge() {
      var badge = document.createElement('span');
      badge.className = 'dd-swatch-card__restock';
      badge.hidden = true;

      var iconWrap = document.createElement('span');
      iconWrap.className = 'dd-swatch-card__incoming-icon';
      iconWrap.setAttribute('aria-hidden', 'true');
      iconWrap.appendChild(this.createIncomingIcon());

      var text = document.createElement('span');
      text.className = 'dd-swatch-card__restock-text';

      badge.appendChild(iconWrap);
      badge.appendChild(text);
      return badge;
    }

    optionValuesMatch(variantValue, selectedValue, optionName) {
      var variantNorm = this.normalizeOptionValue(variantValue, optionName);
      var selectedNorm = this.normalizeOptionValue(selectedValue, optionName);
      if (!variantNorm || !selectedNorm) return false;
      return variantNorm === selectedNorm;
    }

    normalizeOptionValue(value, optionName) {
      var norm = String(value == null ? '' : value)
        .replace(/"/g, '')
        .toLowerCase()
        .trim();

      var optionNameNorm = String(optionName || '').toLowerCase();
      var isLength = optionNameNorm.indexOf('length') !== -1 || optionNameNorm.indexOf('inch') !== -1;

      if (isLength) {
        norm = norm.replace(/\binches\b/g, '').replace(/\binch\b/g, '').trim();
      }

      return norm.replace(/\s+/g, ' ');
    }

    /*
      Toggles sold-out class, restock badge, luminance, aria.

      `options.colorHasStockElsewhere` only drives the
      "Available in X, Y" hint. The restock badge is always the
      selected length + this color, even when other lengths are in stock.
    */
    setSoldOutState(card, isSoldOut, options) {
      var colorHasStockElsewhere = !!(options && options.colorHasStockElsewhere);

      card.classList.toggle('is--sold-out', isSoldOut);
      card.classList.toggle('is--combo-unavailable', isSoldOut && colorHasStockElsewhere);

      var imgContainer = card.querySelector('.dd-swatch-card__image');
      var overlay = imgContainer ? imgContainer.querySelector('.dd-swatch-card__stock-overlay') : null;

      if (isSoldOut) {
        card.setAttribute('aria-disabled', 'true');
        card.dataset.available = 'false';

        if (!overlay && imgContainer) {
          overlay = document.createElement('div');
          overlay.className = 'dd-swatch-card__stock-overlay';
          var hintSpan = document.createElement('span');
          hintSpan.className = 'dd-swatch-card__availability-hint';
          hintSpan.setAttribute('data-dd-availability-hint', '');
          hintSpan.style.display = 'none';
          overlay.appendChild(hintSpan);
          imgContainer.appendChild(overlay);
        }

        if (options && options.restockVariant) {
          var restockVariant = options.restockVariant;
          if (restockVariant.id != null) card.dataset.restockVariantId = String(restockVariant.id);
          if (restockVariant.sku) card.dataset.restockSku = String(restockVariant.sku);
        } else {
          var restockId = options && options.restockVariantId;
          if (restockId != null) card.dataset.restockVariantId = String(restockId);
          else delete card.dataset.restockVariantId;
          delete card.dataset.restockSku;
        }
        var leftover = overlay ? overlay.querySelector('.dd-swatch-card__restock-badge') : null;
        if (leftover) leftover.remove();
        var isPreorder = card.classList.contains('is--preorder') ||
          !!(card.querySelector('.dd-swatch-card__incoming:not([hidden])'));
        var dateMd = (!isPreorder && typeof window.getRestockNumericLabel === 'function')
          ? window.getRestockNumericLabel((options && options.restockVariant) || (options && options.restockVariantId))
          : '';
        var notify = card.querySelector('.dd-swatch-card__notify-badge');
        if (notify) {
          this._paintNotifyDate(card, dateMd);
          this._paintRestockCorner(card, '');
        } else {
          this._paintRestockCorner(card, dateMd ? 'BACK BY ' + dateMd : '');
        }
        if (imgContainer) this.detectLuminanceForContainer(imgContainer);
      } else {
        card.removeAttribute('aria-disabled');
        card.dataset.available = 'true';

        var leftover2 = overlay ? overlay.querySelector('.dd-swatch-card__restock-badge') : null;
        if (leftover2) leftover2.remove();
        this._paintRestockCorner(card, '');
        delete card.dataset.restockVariantId;
        delete card.dataset.restockSku;

        /* Keep luminance for corner badges (low-stock) on in-stock cards. */
        if (imgContainer) this.detectLuminanceForContainer(imgContainer);
      }
    }

    showTextureNotice(card) {
      var imgContainer = card.querySelector('.dd-swatch-card__image');
      if (!imgContainer) return;
      var existing = imgContainer.querySelector('.dd-swatch-card__texture-notice');
      if (existing) return;
      var notice = document.createElement('span');
      notice.className = 'dd-swatch-card__texture-notice';
      notice.textContent = 'Not available textured';
      imgContainer.appendChild(notice);
    }

    removeTextureNotice(card) {
      var notice = card.querySelector('.dd-swatch-card__texture-notice');
      if (notice) notice.remove();
    }

    setLeadTimeBadge(card, showLeadTime) {
      var imgContainer = card.querySelector('.dd-swatch-card__image');
      var badgeParent = imgContainer || card;

      if (showLeadTime) {
        var badge = badgeParent.querySelector('.dd-swatch-card__lead-badge');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'dd-swatch-card__lead-badge';
          badge.setAttribute('data-dd-lead-badge', '');
          badge.textContent = 'Ships 6-8 Weeks';
          badgeParent.appendChild(badge);
        }
        badge.classList.add('is-visible');
        if (imgContainer) this.detectLuminanceForContainer(imgContainer);
      } else {
        var badge2 = badgeParent.querySelector('.dd-swatch-card__lead-badge');
        if (badge2) badge2.classList.remove('is-visible');
      }
    }

    detectLuminanceForContainer(imgContainer) {
      var img = imgContainer.querySelector('img');
      if (!img || !img.src || img.src === '' || img.src === 'null') {
        imgContainer.setAttribute('data-luminance', 'dark');
        imgContainer.setAttribute('data-luminance-corner', 'dark');
        var card = imgContainer.closest('.dd-swatch-card');
        if (card) {
          card.setAttribute('data-luminance', 'dark');
          card.setAttribute('data-luminance-corner', 'dark');
        }
        return;
      }
      if (this.luminanceCache.has(img.src)) {
        this.applyLuminanceFromCache(img, this.luminanceCache.get(img.src));
        return;
      }
      if (img.complete && img.naturalWidth > 0) {
        this.detectLuminance(img);
      } else {
        var self = this;
        img.addEventListener('load', function () { self.detectLuminance(img); }, { once: true });
      }
    }

    /*
      Storefront-API GraphQL fallback for high-variant products.

      Liquid's `product.variants` is hard-capped at 250. When SuperWeft
      (1056 variants) renders, the variants beyond position 250 are
      simply absent from `[data-dd-variant-data]` and therefore from
      every per-color "Available in 18", 20"" hint computed in Liquid
      AND from this.variantData on the client. Length pills 22"/24"
      stop appearing in hint text even when those variants are in stock.

      This method bridges the gap. When the DOM-supplied variantData
      length is at the cap (≥250), call window.ddVariantLoader (cursor-
      paginated Storefront API GraphQL) to fetch the full set, cache
      it on this._fullVariantData, then refresh hint text on every
      sold-out color card so the "Available in" label reflects every
      length the color actually ships in.

      Defensive guards:
       - No-op if loader script absent (cart-only contexts)
       - No-op if not capped (small products — wasteful)
       - No-op if no product handle attribute (snippet misconfigured)
       - In-flight flag prevents double-fires on rapid swaps
       - Loader returns [] on failure → never overwrite real data
       - Loader caches in sessionStorage for 5 minutes → repeat
         swaps don't re-hit the Storefront API
    */
    _loadFullVariantsAndRefreshHints() {
      if (!window.ddVariantLoader) return;
      if (this._fullVariantData) {
        this._refreshSoldOutCardHints();
        return;
      }
      if (this._fullVariantFetchInflight) return;
      if (this.variantData.length < 250) return;
      var handle = this.dataset.productHandle;
      if (!handle) return;

      this._fullVariantFetchInflight = true;
      var self = this;
      window.ddVariantLoader.fetch(handle).then(function (full) {
        self._fullVariantFetchInflight = false;
        if (!Array.isArray(full) || full.length <= self.variantData.length) return;
        self._fullVariantData = full;
        self.variantData = full;
        self._refreshSoldOutCardHints();
        self.syncDrawerPrices();
        if (self._pendingStockedLanding) {
          self.ensureStockedLandingVariant();
        }
      }).catch(function (err) {
        self._fullVariantFetchInflight = false;
        try { console.warn('[dd-swatch-selector] variant loader failed', err && err.message); } catch (e) { /* */ }
      });
    }

    _refreshSoldOutCardHints() {
      var colorGroup = this.getColorGroup();
      if (!colorGroup) return;
      var colorOptName = colorGroup.dataset.optionName;
      var colorPos = this.getPositionForOptionName(colorOptName);
      if (!colorPos) return;
      var cards = colorGroup.querySelectorAll('[data-dd-swatch-item]');
      var self = this;
      self.applyRestockBadges();
      self.applyPreorderBadges();
      self.refreshCornerLuminance();
      cards.forEach(function (card) {
        var colorVal = card.dataset.value;
        if (!colorVal) return;
        self.updateCardHint(card, colorVal, colorPos, false);
      });

      // Sticky-footer ATC reads getAvailableLengthsText() inside its
      // syncButtonFromRenderedHtml — which only fires on variant
      // changes. After GraphQL load lands the full variant set (and
      // updates the custom-length filter contract), nudge the sticky
      // to re-sync so users who never touch the picker still see the
      // corrected "Available in" list. No-op when no current variant
      // (the sticky guards against null).
      var sticky = document.querySelector('dd-sticky-footer');
      if (sticky && typeof sticky.syncButtonFromRenderedHtml === 'function') {
        var pi = this.closest('product-info');
        var currentVariantId = sticky.currentVariantId
          || (pi && pi.querySelector('input[name="id"]') && pi.querySelector('input[name="id"]').value);
        var v = currentVariantId
          ? this.variantData.find(function (x) { return String(x.id) === String(currentVariantId); })
          : null;
        if (v) {
          try { sticky.syncButtonFromRenderedHtml(null, v); } catch (e) { /* */ }
        }
      }

      var productInfo = this.closest('product-info');
      var submitBtn = productInfo && productInfo.querySelector('.product-form__submit');
      if (submitBtn && typeof this.applyAtcAvailabilityOnlyState === 'function') {
        try { this.applyAtcAvailabilityOnlyState(submitBtn); } catch (e) { /* */ }
      }

      try {
        this.dispatchEvent(new CustomEvent('dd:variants-full', { bubbles: true }));
      } catch (e) { /* */ }
    }

    /* Buffered customer ISO for the pre-order promise on this card's
       current combo, or '' when the ddPreorder map has none (yet). */
    _comboIncomingIso(colorVal, colorPos) {
      if (!window.ddPreorder || typeof window.ddPreorder.get !== 'function' || !colorPos) return '';
      var matches = this.getMatchingVariantsForColor(colorVal, colorPos);
      for (var i = 0; i < matches.length; i++) {
        var iso = window.ddPreorder.get(matches[i].id);
        if (iso) return iso;
      }
      return '';
    }

    _comboIsIncoming(card, colorVal, colorPos) {
      var incoming = card && card.querySelector('.dd-swatch-card__incoming');
      if (incoming && !incoming.hidden) return true;
      return !!this._comboIncomingIso(colorVal, colorPos);
    }

    /*
      Pre-order cards: corner badge carries the date ("INCOMING
      10/14", see applyPreorderBadges), the hint overlay carries the
      action ("Pre-order available").
    */
    _paintIncomingHint(hintEl) {
      hintEl.textContent =
        window.ddPreorder && typeof window.ddPreorder.incomingHint === 'function'
          ? window.ddPreorder.incomingHint()
          : 'Pre-order available';
      hintEl.style.display = 'flex';
    }

    _variantIsIncoming(variant) {
      if (!variant || !window.ddPreorder || typeof window.ddPreorder.get !== 'function') return false;
      return !!window.ddPreorder.get(variant.id);
    }

    updateCardHint(card, colorVal, colorPos, comboAvailable) {
      var hintEl = card.querySelector('[data-dd-availability-hint]');
      var incomingOn = this._comboIsIncoming(card, colorVal, colorPos);

      if (comboAvailable && !incomingOn) {
        this._paintLowStockHint(card);
        return;
      }

      if (!incomingOn && !card.classList.contains('is--sold-out')) {
        if (hintEl) hintEl.style.display = 'none';
        return;
      }

      if (!hintEl) return;

      if (incomingOn) {
        this._paintIncomingHint(hintEl);
        return;
      }

      var availableLengths = this.getInStockLengths(colorVal, colorPos);

      if (availableLengths.length > 0) {
        hintEl.textContent = 'Available in ' + availableLengths.join(', ');
        hintEl.style.display = 'flex';
      } else {
        hintEl.style.display = 'none';
      }
    }

    _isCustomOptionValue(value) {
      if (!value || !this.customOptionNameTokens || !this.customOptionNameTokens.length) return false;
      var down = String(value).toLowerCase();
      for (var i = 0; i < this.customOptionNameTokens.length; i++) {
        if (down.indexOf(this.customOptionNameTokens[i]) !== -1) return true;
      }
      return false;
    }

    _getLengthPosition() {
      var groups = this.querySelectorAll('[data-dd-option-group]');
      for (var g = 0; g < groups.length; g++) {
        if (groups[g].dataset.optionName.toLowerCase().indexOf('length') !== -1) {
          return parseInt(groups[g].dataset.optionPosition, 10) || null;
        }
      }
      return null;
    }

    /*
      ───────────────────────────────────────────────────────
      Shared in-stock-length contract.

      One list, three consumers: the swatch-card hint
      ("Available in 18", 20""), the ATC / sticky "Available in"
      face (getAvailableLengthsText), and the custom-acceptance
      "In stock" row (dd-custom-gate.js). Mirrors the Liquid
      filter in snippets/dd-swatch-option-group.liquid so the
      initial paint and every JS refresh agree.

      A length counts as in stock when a variant at that length
      is available, is not a back-ordered continue-selling
      variant (policy=continue AND qty<=0; qty>0 or untracked
      still counts), is not a pre-order promise, and carries no
      CUSTOM option value on any axis (lengths 14, 16, 26, 28...
      or textures like "2 - Medium Wave (2B)"). Custom builds are
      made to order and never belong in a "ships now" claim.

      With a custom texture selected every matching variant is
      custom, so the list is empty by construction and the
      consumers hide.

      colorVal / colorPos pin the color; every other non-length
      option (texture...) follows the current selection. Pass
      neither to use the selected color as well.
      ───────────────────────────────────────────────────────
    */
    getInStockLengths(colorVal, colorPos) {
      var lengthPos = this._getLengthPosition();
      if (!lengthPos) return [];

      var self = this;
      var colorOptionName = colorPos ? this.getOptionNameByPosition(colorPos) : null;
      var lengths = [];
      for (var i = 0; i < this.variantData.length; i++) {
        var v = this.variantData[i];
        if (!v.available) continue;
        if (
          v.inventory_policy === 'continue' &&
          typeof v.inventory_quantity === 'number' &&
          v.inventory_quantity <= 0
        ) continue;
        if (this._variantIsIncoming(v)) continue;

        var match = true;
        for (var k = 1; k <= 3; k++) {
          if (k === lengthPos) continue;
          if (colorPos && k === colorPos) {
            if (!self.optionValuesMatch(v['option' + k], colorVal, colorOptionName)) { match = false; break; }
            continue;
          }
          var optName = self.getOptionNameByPosition(k);
          if (!optName) continue;
          var sel = self.selectedValues[optName];
          if (!sel) continue;
          if (!self.optionValuesMatch(v['option' + k], sel, optName)) { match = false; break; }
        }
        if (!match) continue;

        var len = v['option' + lengthPos];
        if (!len) continue;
        // A variant carrying ANY custom option value (custom length,
        // custom texture such as "2 - Medium Wave (2B)") is made to
        // order and never ships now, even when Shopify reports it
        // available (untracked or continue-selling stock). Substring
        // match: the option value is "14"" / "26 inches" and the
        // custom-values token is just "14" / "26".
        var isCustomVariant = false;
        for (var c = 1; c <= 3; c++) {
          if (this._isCustomOptionValue(v['option' + c])) { isCustomVariant = true; break; }
        }
        if (isCustomVariant) continue;
        if (lengths.indexOf(len) === -1) lengths.push(len);
      }
      return lengths;
    }

    getAvailableLengthsText() {
      var lengths = this.getInStockLengths();
      return lengths.length ? 'Available in ' + lengths.join(', ') : null;
    }

    /*
      True when the user's currently-selected color (or texture) is in the
      `is--combo-unavailable` state — Drop Dead's signal for "we don't make
      this color/texture combination at all" (deny-policy + OOS on a
      custom-eligible product per setSoldOutState's logic above).

      Distinct from a plain Awaiting-Restock variant: combo-unavailable
      means the brand isn't producing this combo, so a Notify-me makes no
      sense (no future restock to notify about) and the sticky ATC should
      surface "Not offered" instead of "Awaiting Restock". The sticky
      footer (assets/dd-sticky-footer.js → syncButtonFromRenderedHtml)
      reads this signal up-front and routes around the Awaiting-Restock /
      availability-only branches when it's true.

      Single-source-of-truth: we look at the same DOM state the shopper
      already sees on the swatch grid (the `is--combo-unavailable` class
      written by setSoldOutState). Using the rendered class avoids
      reimplementing the brand convention in two places — if the
      "what counts as combo-unavailable" rule ever changes, only the
      class-toggle in setSoldOutState needs to move.

      Both swatch-card and pill markers carry the class (some option
      groups render as pills, e.g. Texture on certain templates), so we
      query both selected forms.
    */
    isCurrentComboUnavailable() {
      return !!this.querySelector(
        '[data-dd-swatch-item].is--selected.is--combo-unavailable, ' +
        '[data-dd-pill-item].is--selected.is--combo-unavailable'
      );
    }

    getColorGroup() {
      var groups = this.querySelectorAll('[data-dd-option-group]');
      for (var i = 0; i < groups.length; i++) {
        var name = groups[i].dataset.optionName.toLowerCase();
        if (name === 'color' || name === 'colour' || name === 'colors') {
          return groups[i];
        }
      }
      return null;
    }

    getPositionForOptionName(optName) {
      var group = this.querySelector('[data-dd-option-group][data-option-name="' + optName + '"]');
      return group ? parseInt(group.dataset.optionPosition, 10) : null;
    }

    getOptionNameByPosition(pos) {
      var group = this.querySelector('[data-dd-option-group][data-option-position="' + pos + '"]');
      return group ? group.dataset.optionName : null;
    }

    // ── Luminance detection ─────────────────────────────

    scheduleLuminanceDetection() {
      var cards = this.querySelectorAll('.dd-swatch-card__image img');
      if (!cards.length) return;

      var self = this;
      var run = function () {
        /* Badges first so corner sampling can read pixels under the
           real low-stock / notify pill (see refreshCornerLuminance). */
        self.applyCornerBadges();
        cards.forEach(function (img) {
          if (!img.crossOrigin) img.crossOrigin = 'anonymous';

          if (self.luminanceCache.has(img.src)) {
            self.applyLuminanceFromCache(img, self.luminanceCache.get(img.src));
            return;
          }
          if (img.complete && img.naturalWidth > 0) {
            self.detectLuminance(img);
          } else {
            img.addEventListener('load', function () {
              self.detectLuminance(img);
              self.scheduleCornerLuminanceRefresh();
            }, { once: true });
          }
        });
        self.scheduleCornerLuminanceRefresh();
      };

      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(run);
      } else {
        setTimeout(run, 200);
      }
    }

    _luminanceFromAverage(avg) {
      /* Corner badges: white text (dark corner) unless the sample is
         clearly pale. 105 pulled medium-dark hair into black text and
         made badges hard to read; keep white easier to win on mid tones
         while studio-white / blonde corners (typically 150+) still flip
         to dark-on-light. */
      return avg > 145 ? 'light' : 'dark';
    }

    /* Tight natural-image fallback for object-position: top left when
       the card has no layout size yet. Keep it small so a diagonal
       seam/hair band cannot dominate the average. */
    _sampleNaturalTopLeftCorner(img) {
      var nw = img.naturalWidth;
      var nh = img.naturalHeight;
      if (!nw || !nh) return null;
      return this._sampleImageRegion(
        img,
        Math.floor(nw * 0.02),
        Math.floor(nh * 0.02),
        Math.max(16, Math.floor(nw * 0.12)),
        Math.max(12, Math.floor(nh * 0.08))
      );
    }

    /*
      Decode via a dedicated CORS Image so canvas sampling works even
      when the visible <img> was painted from a non-CORS cache entry.
      Same pattern as dd-founder-message.js.
    */
    _withCorsImage(src, callback) {
      if (!src) return;
      var probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = function () {
        try {
          callback(probe);
        } catch (e) {
          /* tainted / decode race */
        }
      };
      probe.onerror = function () {
        /* ignore — leave prior corner attr */
      };
      probe.src = src;
    }

    scheduleCornerLuminanceRefresh() {
      var self = this;
      var run = function () {
        self.refreshCornerLuminance();
      };
      /* Two rAFs: wait until appendChild badges have a real box. */
      requestAnimationFrame(function () {
        requestAnimationFrame(run);
      });
      setTimeout(run, 120);
      setTimeout(run, 400);
    }

    _parseObjectPosition(str) {
      if (!str) return { x: 0, y: 0 };
      var parts = String(str).trim().split(/\s+/);
      function toRel(p) {
        if (!p) return 0.5;
        if (p.indexOf('%') !== -1) return parseFloat(p) / 100;
        if (p === 'left' || p === 'top') return 0;
        if (p === 'right' || p === 'bottom') return 1;
        if (p === 'center') return 0.5;
        var n = parseFloat(p);
        return isNaN(n) ? 0.5 : n;
      }
      return { x: toRel(parts[0]), y: toRel(parts[1] || parts[0]) };
    }

    /*
      Map a layout rect (relative to the image container) through the
      img's object-fit / object-position crop into natural-image coords.
      Same approach as dd-before-after-gallery probeLumUnderTag and
      dd-founder-message screenPointToImagePixel — samples what the
      shopper actually sees behind the badge, not a fixed % of the file.
    */
    _layoutRectToNaturalRegion(img, left, top, width, height) {
      var container = img.closest('.dd-swatch-card__image') || img.parentElement;
      var figW = container ? container.clientWidth : 0;
      var figH = container ? container.clientHeight : 0;
      var nw = img.naturalWidth;
      var nh = img.naturalHeight;
      if (!figW || !figH || !nw || !nh || width <= 0 || height <= 0) return null;

      var cs = window.getComputedStyle(img);
      var fit = cs.objectFit || 'cover';
      var pos = this._parseObjectPosition(cs.objectPosition || '0% 0%');
      var scale;
      if (fit === 'contain' || fit === 'scale-down') {
        scale = Math.min(figW / nw, figH / nh);
        if (fit === 'scale-down' && scale > 1) scale = 1;
      } else if (fit === 'none') {
        scale = 1;
      } else {
        scale = Math.max(figW / nw, figH / nh);
      }
      if (!scale || !isFinite(scale)) return null;

      var drawnW = nw * scale;
      var drawnH = nh * scale;
      var offsetX = (figW - drawnW) * pos.x;
      var offsetY = (figH - drawnH) * pos.y;

      var sx = (left - offsetX) / scale;
      var sy = (top - offsetY) / scale;
      var sw = width / scale;
      var sh = height / scale;

      sx = Math.max(0, Math.min(nw - 1, sx));
      sy = Math.max(0, Math.min(nh - 1, sy));
      sw = Math.max(1, Math.min(nw - sx, sw));
      sh = Math.max(1, Math.min(nh - sy, sh));
      return { sx: sx, sy: sy, sw: sw, sh: sh };
    }

    _probeRectInContainer(img, container) {
      if (!img || !container) return null;
      var probe =
        container.querySelector('.dd-swatch-card__low-badge') ||
        container.querySelector('.dd-swatch-card__notify-badge') ||
        container.querySelector('.dd-swatch-card__incoming:not([hidden])') ||
        container.querySelector('[data-dd-corner-badges]');

      if (probe && probe.offsetWidth > 0 && probe.offsetHeight > 0) {
        var cRect = container.getBoundingClientRect();
        var bRect = probe.getBoundingClientRect();
        return {
          left: bRect.left - cRect.left,
          top: bRect.top - cRect.top,
          width: bRect.width,
          height: bRect.height
        };
      }

      /* Fallback when no corner badge yet: CSS placement of
         .dd-swatch-card__corner-badges (top/left 0.8rem, h 2.4rem). */
      var rootFs = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 10;
      var inset = 0.8 * rootFs;
      var badgeH = 2.4 * rootFs;
      return {
        left: inset,
        top: inset,
        width: Math.min(container.clientWidth * 0.42, 7.2 * rootFs),
        height: badgeH
      };
    }

    _sampleBadgeZoneLuminance(img) {
      var container = img.closest('.dd-swatch-card__image');
      var rect = this._probeRectInContainer(img, container);
      if (rect) {
        var region = this._layoutRectToNaturalRegion(
          img,
          rect.left,
          rect.top,
          rect.width,
          rect.height
        );
        if (region) {
          return this._sampleImageRegion(img, region.sx, region.sy, region.sw, region.sh);
        }
      }
      /* Layout not ready (0×0 card) or probe missed — never invent
         "dark" here; that painted white badges on pale product shots
         and stuck via luminanceCache. Natural top-left matches
         object-position: top left. */
      return this._sampleNaturalTopLeftCorner(img);
    }

    _applyCornerLuminance(card, container, img, cornerLum) {
      if (!cornerLum) return;
      container.setAttribute('data-luminance-corner', cornerLum);
      card.setAttribute('data-luminance-corner', cornerLum);
      var cached = this.luminanceCache.get(img.src);
      if (cached && typeof cached === 'object') {
        cached.corner = cornerLum;
      } else if (img.src) {
        this.luminanceCache.set(img.src, {
          overlay: (cached && cached.overlay) || cornerLum,
          corner: cornerLum
        });
      }
    }

    /*
      Re-sample corner contrast for every color card that has a visible
      corner badge. Layout-aware (badge box → cover crop → natural pixels).
      Does not touch overlay luminance.
    */
    refreshCornerLuminance() {
      var cards = this.querySelectorAll('.dd-swatch-group--color [data-dd-swatch-item]');
      if (!cards.length) return;
      var self = this;
      cards.forEach(function (card) {
        var container = card.querySelector('.dd-swatch-card__image');
        if (!container) return;
        var hasCorner =
          container.querySelector('.dd-swatch-card__low-badge') ||
          container.querySelector('.dd-swatch-card__notify-badge') ||
          container.querySelector('.dd-swatch-card__incoming:not([hidden])');
        if (!hasCorner) return;
        var img = container.querySelector('img');
        if (!img || !img.src) return;

        var applyFrom = function (sourceImg) {
          if (!sourceImg || !sourceImg.naturalWidth) return;
          try {
            var cornerLum = self._sampleBadgeZoneLuminance(sourceImg);
            /* Re-probe with the visible img for layout→natural mapping
               when the source is a detached CORS probe. */
            if (sourceImg !== img && container.clientWidth > 0) {
              var rect = self._probeRectInContainer(img, container);
              if (rect) {
                var region = self._layoutRectToNaturalRegion(
                  img,
                  rect.left,
                  rect.top,
                  rect.width,
                  rect.height
                );
                if (region) {
                  cornerLum = self._sampleImageRegion(
                    sourceImg,
                    region.sx,
                    region.sy,
                    region.sw,
                    region.sh
                  );
                }
              }
            }
            self._applyCornerLuminance(card, container, img, cornerLum);
          } catch (e) {
            /* CORS / tainted — try probe path below */
          }
        };

        if (img.naturalWidth > 0) {
          try {
            if (!img.crossOrigin) img.crossOrigin = 'anonymous';
            applyFrom(img);
            return;
          } catch (e) {
            /* fall through to CORS probe */
          }
        }
        self._withCorsImage(img.currentSrc || img.src, applyFrom);
      });
    }

    _sampleImageRegion(img, sx, sy, sampleW, sampleH) {
      if (!img || !img.naturalWidth) return null;
      if (sx < 0) sx = 0;
      if (sy < 0) sy = 0;
      sampleW = Math.min(sampleW, img.naturalWidth - sx);
      sampleH = Math.min(sampleH, img.naturalHeight - sy);
      if (sampleW <= 0 || sampleH <= 0) return null;

      /* Downscale before averaging (same idea as before/after gallery).
         Use median so a thin dark seam through a pale corner cannot
         drag the mean under the light/dark threshold. */
      var outW = Math.max(8, Math.min(32, Math.round(sampleW)));
      var outH = Math.max(4, Math.min(16, Math.round(sampleH)));
      var canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, sx, sy, sampleW, sampleH, 0, 0, outW, outH);
      var data = ctx.getImageData(0, 0, outW, outH).data;
      var values = [];
      for (var i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 16) continue;
        values.push(data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114);
      }
      if (!values.length) return null;
      values.sort(function (a, b) { return a - b; });
      var mid = values[Math.floor(values.length / 2)];
      return this._luminanceFromAverage(mid);
    }

    detectLuminance(img) {
      try {
        var nw = img.naturalWidth;
        var nh = img.naturalHeight;
        var container = img.closest('.dd-swatch-card__image');
        var overlayLum;

        /* Bottom-center of the *visible* crop for stock overlay copy. */
        if (container && container.clientWidth > 0 && container.clientHeight > 0) {
          var figW = container.clientWidth;
          var figH = container.clientHeight;
          var overlayRegion = this._layoutRectToNaturalRegion(
            img,
            (figW - 40) / 2,
            figH - 20,
            40,
            20
          );
          overlayLum = overlayRegion
            ? this._sampleImageRegion(
                img,
                overlayRegion.sx,
                overlayRegion.sy,
                overlayRegion.sw,
                overlayRegion.sh
              )
            : this._sampleImageRegion(img, (nw - 40) / 2, nh - 20, 40, 20);
        } else {
          overlayLum = this._sampleImageRegion(img, (nw - 40) / 2, nh - 20, 40, 20);
        }

        /* Corner: pixels under low-stock / notify badge (layout-aware). */
        var cornerLum = this._sampleBadgeZoneLuminance(img);

        this.luminanceCache.set(img.src, { overlay: overlayLum, corner: cornerLum });
        this.applyLuminance(img, overlayLum, cornerLum);
      } catch (e) {
        /* Visible <img> may be a non-CORS cache hit — probe and retry. */
        var self = this;
        this._withCorsImage(img.currentSrc || img.src, function (probe) {
          try {
            var cornerLum = self._sampleBadgeZoneLuminance(probe);
            var overlayLum = self._sampleImageRegion(
              probe,
              (probe.naturalWidth - 40) / 2,
              probe.naturalHeight - 20,
              40,
              20
            );
            self.luminanceCache.set(img.src, { overlay: overlayLum, corner: cornerLum });
            self.applyLuminance(img, overlayLum, cornerLum);
          } catch (err) {
            /* give up */
          }
        });
      }
    }

    applyLuminanceFromCache(img, cached) {
      if (typeof cached === 'string') {
        this.applyLuminance(img, cached, cached);
        return;
      }
      this.applyLuminance(img, cached.overlay, cached.corner);
    }

    applyLuminance(img, overlayLum, cornerLum) {
      var imgContainer = img.closest('.dd-swatch-card__image');
      var card = img.closest('.dd-swatch-card');
      /* Do not fall corner back to overlay (bottom-center hair is usually
         dark). That used to force white badges onto pale top-left corners. */
      if (imgContainer) {
        if (overlayLum) imgContainer.setAttribute('data-luminance', overlayLum);
        if (cornerLum) imgContainer.setAttribute('data-luminance-corner', cornerLum);
      }
      if (card) {
        if (overlayLum) card.setAttribute('data-luminance', overlayLum);
        if (cornerLum) card.setAttribute('data-luminance-corner', cornerLum);
      }
    }

    // ── Drawer item selection (called by dd-option-drawer.js) ──

    handleDrawerItemSelect(optionName, value, optionValueId, selectedDrawerItem) {
      this.selectedValues[optionName] = value;
      this.customOptionSelections[optionName] = true;

      var group = this.querySelector('[data-dd-option-group][data-option-name="' + optionName + '"]');
      if (group) {
        group.querySelectorAll('[data-dd-swatch-item], [data-dd-pill-item]').forEach(function (el) {
          el.classList.remove('is--selected');
          el.setAttribute('aria-pressed', 'false');
        });
      }

      var self = this;
      requestAnimationFrame(function () { self._updateToggleSliders(); });
      this.updateSelectedLabel(optionName, value);
      if (selectedDrawerItem) this.updateGramDisplay(selectedDrawerItem);
      this.updateBundleHint(optionName, value);
      this.updateSelectionGate();
      this.updateCustomNotices();
      /* Section Rendering API swap (via variantChange) delivers authoritative Liquid state. */
      this.applyCornerBadges();
      this.syncInlineBuyButtonsForCombo();
      var variant = this.findVariantFromSelected();
      if (variant) this.applyOptimisticUpdates(variant);
      this.commitToHiddenPicker(optionName, optionValueId);
    }

    initToggleSliders() {
      var self = this;
      var containers = this.querySelectorAll(
        '.dd-swatch-group--length .dd-swatch-pills, ' +
        '.dd-swatch-group--texture .dd-swatch-pills'
      );

      containers.forEach(function (container) {
        if (container.querySelector('.dd-toggle-slider')) return;

        var slider = document.createElement('span');
        slider.className = 'dd-toggle-slider';
        slider.setAttribute('aria-hidden', 'true');
        slider.setAttribute('data-no-transition', '');
        container.prepend(slider);

        var ro = new ResizeObserver(function () {
          if (container.offsetWidth > 0) {
            ro.disconnect();
            self._positionToggleSlider(container, slider);
            requestAnimationFrame(function () {
              slider.removeAttribute('data-no-transition');
            });
          }
        });
        ro.observe(container);
      });

      if (!this._toggleResizeBound) {
        this._toggleResizeBound = true;
        var resizeTimer;
        window.addEventListener('resize', function () {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(function () { self._updateToggleSliders(true); }, 100);
        });
      }
    }

    _positionToggleSlider(container, slider) {
      if (!slider) return;
      var selected = container.querySelector('.dd-swatch-pill.is--selected');
      if (!selected || selected.offsetWidth === 0) {
        slider.style.opacity = '0';
        return;
      }

      slider.style.opacity = '1';
      slider.style.width = selected.offsetWidth + 'px';
      slider.style.height = selected.offsetHeight + 'px';
      slider.style.top = selected.offsetTop + 'px';
      slider.style.transform = 'translateX(' + selected.offsetLeft + 'px)';
    }

    _updateToggleSliders(noTransition) {
      var self = this;
      this.querySelectorAll('.dd-toggle-slider').forEach(function (slider) {
        var container = slider.parentElement;
        if (!container) return;
        if (noTransition) slider.setAttribute('data-no-transition', '');
        self._positionToggleSlider(container, slider);
        if (noTransition) {
          requestAnimationFrame(function () { slider.removeAttribute('data-no-transition'); });
        }
      });
    }

    disconnectedCallback() {
      if (this._onAtcMarqueeResize) {
        window.removeEventListener('resize', this._onAtcMarqueeResize);
        this._onAtcMarqueeResize = null;
      }
      if (this._atcMarqueeResizeTimer) {
        clearTimeout(this._atcMarqueeResizeTimer);
        this._atcMarqueeResizeTimer = null;
      }
    }
  }

  customElements.define('dd-swatch-selector', DdSwatchSelector);
})();
